/* GS LEVEL 4 — 夜班呼叫器：外科病房值班（sentinel bleed after Whipple / pager triage） */
import {
  addWait,
  annoy,
  ask,
  C,
  type Cell,
  chat,
  type ChoiceItem,
  clamp,
  clearWait,
  clock,
  closeZoom,
  type Ctx,
  dither,
  doc,
  DRAW,
  type DrawFn,
  drawEcg,
  drawPleth,
  ent,
  eventBanner,
  finding,
  getImagery,
  type Hotspot,
  incomingCall,
  INK,
  type Level,
  leaveBy,
  me,
  mkEnt,
  note,
  obox,
  openDialog,
  openMonitor,
  openZoom,
  pell,
  pipSet,
  playEnding,
  pline,
  post,
  ptext,
  R,
  renderSide,
  session,
  spawnAt,
  spend,
  strList,
  T,
  tr,
  walkEnt,
  type ZoomCfg,
} from "../../engine";
import { type BedAnim, bedAnimPos, bedHots, drawBedScene } from "../shared/bedside";
import { photoOnly, sideMark } from "../shared/imaging";
import { isTripUrgency, startVsTrip, type TripUrgency, type VsTrip } from "../shared/vs-trip";

/* ---------- static data ---------- */

type Room = "701" | "702" | "703";
const ROOM_DLG: Record<Room, string> = { "701": "bedA", "702": "bedB", "703": "bedC" };
const ROOM_NAME: Record<Room, string> = tr(
  { "701": "701 鄭伯伯", "702": "702 李伯伯", "703": "703 吳太太" },
  { "701": "701 Mr. Cheng Sr.", "702": "702 Mr. Li", "703": "703 Mrs. Wu" },
);
/** Where the nurse stands to work on each bed, and her desk. */
const SPOT: Record<Room | "st", Cell> = { "701": [3, 3], "702": [9, 3], "703": [16, 3], st: [4, 9] };
const DOOR: Cell = [17, 11];
const WU_HOME: Cell = [15, 1];
const TO_ELEVATOR: Cell[] = [
  [15, 1],
  [15, 3],
  [16, 3],
  [16, 7],
  [17, 7],
  [17, 10],
];

/** Timeline (game minutes after 02:00). */
const PAGE_A = 1;
const PAGE_B = 4;
const PAGE_C = 9;
const REBLEED = 24;
const BIG_BLEED = 42;
const IR_PREP = 12;

/** 吳太太 — what she (or others) can reveal. */
const WU_FACTS = {
  melena: tr(
    "一點半左右拉了一次，黑黑的、黏黏的，像柏油，很臭",
    "Around 1:30 she had one bowel movement: black, sticky, like tar, very foul-smelling",
  ),
  dizzy: tr(
    "去廁所站起來的時候眼前一黑，扶著牆才沒跌倒",
    "When she stood up in the bathroom everything went black; she only stayed up by holding on to the wall",
  ),
  earlier: tr(
    "傍晚就有看到管子裡有一點點紅紅的，以為是正常的，沒講",
    "Back in the early evening she noticed a little red in the tube, thought it was normal and didn't mention it",
  ),
  pain: tr(
    "肚子右上面引流管那邊比較脹痛，今天比昨天多一點",
    "The upper right of her belly around the drain feels bloated and sore, a bit more today than yesterday",
  ),
  nausea: tr("有點噁心想吐，還沒吐", "A little nauseous, hasn't vomited yet"),
  shy: tr(
    "不想吵醫生，大家半夜都在睡",
    "Doesn't want to bother the doctors; everyone's asleep in the middle of the night",
  ),
  fever: tr("前幾天有發燒，這兩天沒有了", "Had a fever a few days ago, none the last two days"),
  meds: tr(
    "每天晚上護理師會在肚皮打一針（預防血栓）",
    "Every evening the nurse gives her a shot in the belly (to prevent clots)",
  ),
} as const;
type WuFact = keyof typeof WU_FACTS;
const isWuFact = (x: string): x is WuFact => x in WU_FACTS;

/** 李伯伯 */
const LI_FACTS = {
  full: tr(
    "下面很脹、一直想尿尿，可是尿不出來",
    "Feels really full down below, keeps needing to pee but nothing comes out",
  ),
  turned: tr(
    "十二點多自己翻身側睡，壓到東西",
    "A little after midnight he rolled onto his side by himself and lay on something",
  ),
  thirsty: tr("口很渴", "Very thirsty"),
  pain: tr("傷口痛還可以，有按止痛（PCA）", "The wound pain is bearable; he's been pressing the pain button (PCA)"),
} as const;
type LiFact = keyof typeof LI_FACTS;
const isLiFact = (x: string): x is LiFact => x in LI_FACTS;

/** 鄭先生 (701 家屬) */
const ZH_FACTS = {
  beep: tr(
    "點滴機一直嗶嗶叫，叫了快半小時，按鈴都沒人來",
    "The IV pump has been beeping nonstop for almost half an hour, and nobody comes when he presses the call bell",
  ),
  delirium: tr(
    "爸爸前天開完刀那晚有亂講話，以為自己在家，還把點滴拔掉",
    "The night after surgery, the day before yesterday, his dad was talking nonsense, thought he was at home, and pulled out his IV",
  ),
  saw703: tr(
    "一點四十幾分在走廊看到 703 那個阿姨扶著牆從廁所出來，臉白得跟紙一樣，差點跌倒",
    "Around 1:40-something he saw the lady from 703 in the hallway coming out of the bathroom holding on to the wall, white as a sheet, and she nearly fell",
  ),
  complain: tr(
    "如果再沒人處理就要打 1999 投訴",
    "If nobody deals with it he's calling the 1999 city hotline to complain",
  ),
} as const;
type ZhFact = keyof typeof ZH_FACTS;
const isZhFact = (x: string): x is ZhFact => x in ZH_FACTS;

/** Nurse tasks: the one night nurse is the bottleneck. */
const TASK = {
  wu_mon: { room: "703", dur: 2, label: tr("703 接監視器", "703 Hook up monitor") },
  wu_vitals: { room: "703", dur: 2, label: tr("703 量生命徵象", "703 Take vitals") },
  wu_iv2: { room: "703", dur: 6, label: tr("703 再打兩條 18G", "703 Two more 18G IVs") },
  wu_labs: {
    room: "703",
    dur: 3,
    label: tr("703 抽血（CBC、凝血、生化、備血）", "703 Draw labs (CBC, coags, chem, type & cross)"),
  },
  wu_ppi: { room: "703", dur: 2, label: "703 Pantoprazole 80 mg IV" },
  wu_fluid: { room: "703", dur: 2, label: tr("703 LR 500 mL 快速滴注", "703 LR 500 mL bolus") },
  wu_ng: { room: "703", dur: 5, label: tr("703 放鼻胃管", "703 Place NG tube") },
  li_scan: { room: "702", dur: 3, label: tr("702 膀胱超音波", "702 Bladder scan") },
  li_foley: { room: "702", dur: 3, label: tr("702 檢查導尿管", "702 Check Foley") },
  li_lasix: { room: "702", dur: 1, label: "702 Furosemide 20 mg IV" },
  li_fluid: { room: "702", dur: 2, label: "702 LR 500 mL" },
  a_pump: { room: "701", dur: 2, label: tr("701 處理點滴機", "701 Fix IV pump") },
  a_stilnox: { room: "701", dur: 1, label: "701 Zolpidem 5 mg" },
  a_calm: {
    room: "701",
    dur: 3,
    label: tr("701 安撫、重新定向、開小燈", "701 Calm, reorient, night light on"),
  },
  a_restrain: { room: "701", dur: 2, label: tr("701 上約束帶", "701 Apply restraints") },
} as const satisfies Record<string, { room: Room; dur: number; label: string }>;
type TaskId = keyof typeof TASK;
const isTask = (x: string): x is TaskId => x in TASK;

/* ---------- session ---------- */

type WuLoc = "ward" | "ct" | "angioWait" | "angio";

interface Vitals {
  sbp: number;
  dbp: number;
  hr: number;
  rr: number;
  spo2: number;
}

interface Page {
  id: string;
  room: Room;
  t: number;
  text: string;
  answeredAt: number | null;
}

interface Vars {
  pages: Page[];
  /** Escalation steps already fired, by key. */
  esc: Record<string, boolean>;

  /* 703 吳太太 */
  wuLoc: WuLoc;
  monitor: boolean;
  vitalsSeen: { t: number; v: Vitals }[];
  drainSeen: number | null;
  stoolSeen: boolean;
  abdDone: boolean;
  wuRev: Set<WuFact>;
  iv2At: number | null;
  labsAt: number | null;
  labsDone: boolean;
  tcReadyAt: number | null;
  npo: boolean;
  holdClexane: boolean;
  ppiAt: number | null;
  fluidAt: number | null;
  ngAt: number | null;
  rebled: boolean;
  /** 江主任 agreed it is a sentinel bleed: CTA / IR may be ordered. */
  approved: boolean;
  ctaAt: number | null;
  ctaReadAt: number | null;
  ctaRead: boolean;
  irAt: number | null;
  irReadyAt: number | null;
  angioAt: number | null;
  angioStarted: number | null;
  bleedAt: number | null;
  crisisActs: string[];
  oBlood: boolean;
  mtp: boolean;
  pressure: boolean;
  bedAnim: (BedAnim & { dir: "out" | "in" }) | null;
  bedOut: boolean;

  /* 702 李伯伯 */
  liScan: boolean;
  liFixedAt: number | null;
  lasixAt: number | null;
  liFluid: boolean;
  liPulled: boolean;
  liRev: Set<LiFact>;
  scannerIn702: boolean;

  /* 701 鄭伯伯 */
  pumpFixedAt: number | null;
  stilnoxAt: number | null;
  delirAt: number | null;
  delirFix: "calm" | "restrain" | null;
  zhOut: boolean;
  zhRev: Set<ZhFact>;

  /* nurse */
  nq: TaskId[];
  ncur: { id: TaskId; until: number } | null;
  nAwayUntil: number | null;

  /* doctors */
  rTalks: number;
  rAdvice: "none" | "ppi" | "escalate";
  vsTalked: boolean;
  vsAccepted: boolean;
  vsAcceptAt: number | null;
  vsArriveAt: number | null;
  vsHere: boolean;
  trip: VsTrip | null;
  irTalked: boolean;

  emrRead: Set<string>;
  boardRead: boolean;
  endAt: number | null;
  endCode: "A" | "B" | "C" | null;
}

const S = session<Vars>();

/* ---------- small helpers ---------- */

const wuHere = (): boolean => S.wuLoc === "ward" && !S.bedOut && !S.bedAnim;
const resolved701 = (): boolean => S.pumpFixedAt !== null && (S.delirAt === null || S.delirFix !== null);
const nurse = () => ent("gs_nurse");
const nurseOut = (): boolean => S.nAwayUntil !== null && S.t < S.nAwayUntil;

function pendingPages(): Page[] {
  return S.pages.filter((p) => p.answeredAt === null);
}

/* ---------- 吳太太 physiology ---------- */

function drainBlood(): number {
  let v = 30;
  if (S.rebled) v += 60;
  if (S.bleedAt !== null) v += Math.min(900, 250 + (S.t - S.bleedAt) * 90);
  return v;
}

function wuVitals(): Vitals {
  let hr = 100 + Math.min(6, S.t * 0.25);
  let sbp = 126 - Math.min(6, S.t * 0.2);
  let dbp = 74;
  let rr = 18;
  let spo2 = 97;
  if (S.rebled) {
    hr = 114;
    sbp = 108;
    dbp = 64;
    rr = 20;
  }
  if (S.fluidAt !== null && S.t >= S.fluidAt + 5) {
    hr -= 5;
    sbp += 6;
  }
  if (S.bleedAt !== null) {
    const k = Math.min(6, S.t - S.bleedAt);
    hr = 128 + k * 4;
    sbp = 86 - k * 4;
    dbp = 50 - k * 3;
    rr = 26;
    spo2 = 93;
    const resus = (S.oBlood ? 1 : 0) + (S.mtp ? 1 : 0) + (S.pressure ? 1 : 0) + (S.iv2At !== null ? 1 : 0);
    sbp += resus * 6;
    dbp += resus * 3;
    hr -= resus * 4;
  }
  return {
    sbp: Math.round(sbp),
    dbp: Math.round(dbp),
    hr: Math.round(clamp(hr, 40, 160)),
    rr,
    spo2,
  };
}

const vtxt = (v: Vitals): string =>
  tr(
    `BP ${v.sbp}/${v.dbp}，HR ${v.hr}，RR ${v.rr}，SpO₂ ${v.spo2}%`,
    `BP ${v.sbp}/${v.dbp}, HR ${v.hr}, RR ${v.rr}, SpO₂ ${v.spo2}%`,
  );

function wuCondition(): string {
  if (S.bleedAt !== null)
    return tr(
      "臉色慘白、冒冷汗、很喘，講話斷斷續續，一直說好冷",
      "ashen, in a cold sweat, very short of breath, talking in broken fragments, keeps saying she's cold",
    );
  if (S.rebled)
    return tr(
      "臉色比剛剛更白，手腳冰冰的，有點喘，講話變小聲",
      "paler than before, cold hands and feet, a little short of breath, her voice has gone quiet",
    );
  return tr("清醒、講話正常，有點累，臉色有點白", "alert, talking normally, a bit tired, a little pale");
}

function seeVitals(ch: string, by: "clerk" | "nurse" = "clerk"): void {
  const v = wuVitals();
  S.vitalsSeen.push({ t: S.t, v });
  if (by === "clerk") {
    me(ch, tr("（量血壓、心跳、呼吸、血氧）", "(Checks BP, pulse, breathing, SpO₂)"));
    post(ch, "result", null, vtxt(v));
  } else post(ch, "result", "gs_nurse", tr(`703 生命徵象：${vtxt(v)}`, `703 vitals: ${vtxt(v)}`));
}

/* ---------- pager ---------- */

function page(id: string, room: Room, text: string, short: string, until: number, urgent = false): void {
  if (!S.live || S.ended || S.ending) return;
  const here = S.dialog === ROOM_DLG[room];
  S.pages.push({ id, room, t: S.t, text, answeredAt: here ? S.t : null });
  post("pager", "alert", "gs_nurse", tr(`【公務機】${text}`, `[Pager] ${text}`));
  if (here) return;
  addWait({
    id: `pg_${room}`,
    text: tr(`${room}　${short}`, `${room} · ${short}`),
    dlg: ROOM_DLG[room],
    untilT: S.t + until,
    urgent,
    icon: "☎",
    clearOn: "open",
  });
}

/** Opening a bed answers every page from that room. */
function answer(room: Room): void {
  for (const p of S.pages) if (p.room === room && p.answeredAt === null) p.answeredAt = S.t;
}

/* ---------- nurse queue ---------- */

const NURSE_ACK: Partial<Record<TaskId, string>> = tr(
  {
    wu_iv2: "好，兩條 18G，我去打。",
    wu_labs: "CBC、PT/aPTT、生化跟備血，抽了送急件。",
    wu_mon: "我把監視器推過去接。",
    li_lasix: "Lasix 20 喔？好。",
    a_stilnox: "Stilnox 5 mg……他 82 歲耶。你確定的話我就給。",
    a_restrain: "要綁喔……好吧，我去拿約束帶，要記得寫約束紀錄。",
  },
  {
    wu_iv2: "OK, two 18Gs. On it.",
    wu_labs: "CBC, PT/aPTT, chem and type & cross. Drawing now, sending STAT.",
    wu_mon: "I'll wheel the monitor over and hook her up.",
    li_lasix: "Lasix 20? OK.",
    a_stilnox: "Zolpidem 5 mg… he’s 82, you know. If you’re sure, I’ll give it.",
    a_restrain: "Restraints, huh… fine, I’ll get them. Don’t forget to write the restraint note.",
  },
);

function nurseDo(id: TaskId, ch: string): void {
  if (S.ncur?.id === id || S.nq.includes(id) || taskDone(id)) {
    note(tr("這件事欣怡已經在做或做過了。", "Hsin-Yi is already on that, or it’s done."), ch);
    return;
  }
  if (id === "a_stilnox" && !S.esc.stilnoxWarn) {
    S.esc.stilnoxWarn = true;
    me(ch, tr(`（請欣怡：${TASK[id].label}）`, `(Asks Hsin-Yi: ${TASK[id].label})`));
    ask(ch, "gs_nurse", NURSE_ACK.a_stilnox ?? "");
    return;
  }
  S.nq.push(id);
  me(ch, tr(`（請欣怡：${TASK[id].label}）`, `(Asks Hsin-Yi: ${TASK[id].label})`));
  const ahead = (S.ncur ? 1 : 0) + S.nq.length - 1;
  const line =
    NURSE_ACK[id] ??
    (ahead > 0
      ? tr(`好，我手上還有 ${ahead} 件，做完就去。`, `OK. I’ve got ${ahead} ahead of it, I’ll get there after.`)
      : nurseOut()
        ? tr("我還在 CT 室，回去馬上弄。", "I’m still down in CT. I’ll do it as soon as I’m back.")
        : tr("好。", "OK."));
  post(
    ch,
    "npc",
    "gs_nurse",
    ahead > 0 && NURSE_ACK[id] ? tr(`${line}（前面還有 ${ahead} 件）`, `${line} (${ahead} ahead of it)`) : line,
  );
  spend(1);
}

function taskDone(id: TaskId): boolean {
  switch (id) {
    case "wu_mon":
      return S.monitor;
    case "wu_iv2":
      return S.iv2At !== null;
    case "wu_labs":
      return S.labsAt !== null;
    case "wu_ppi":
      return S.ppiAt !== null;
    case "wu_fluid":
      return S.fluidAt !== null;
    case "wu_ng":
      return S.ngAt !== null;
    case "li_scan":
      return S.liScan;
    case "li_foley":
      return S.liFixedAt !== null;
    case "li_lasix":
      return S.lasixAt !== null;
    case "li_fluid":
      return S.liFluid;
    case "a_pump":
      return S.pumpFixedAt !== null;
    case "a_stilnox":
      return S.stilnoxAt !== null;
    case "a_calm":
    case "a_restrain":
      return S.delirFix !== null;
    case "wu_vitals":
      return false;
  }
}

function finishTask(id: TaskId): void {
  const room = TASK[id].room;
  const ch = ROOM_DLG[room];
  switch (id) {
    case "wu_mon":
      if (!wuHere()) return;
      S.monitor = true;
      seeVitals(ch, "nurse");
      post(
        ch,
        "narr",
        null,
        tr("欣怡把監視器推進 703 接上。", "Hsin-Yi wheels the monitor into 703 and hooks her up."),
      );
      break;
    case "wu_vitals":
      if (!wuHere()) return;
      seeVitals(ch, "nurse");
      break;
    case "wu_iv2":
      if (!wuHere()) return;
      S.iv2At = S.t;
      post(ch, "npc", "gs_nurse", tr("兩條 18G 都打好了，一手一條。", "Both 18Gs are in, one in each arm."));
      break;
    case "wu_labs":
      if (!wuHere()) return;
      S.labsAt = S.t;
      S.tcReadyAt = S.t + 35;
      post(
        ch,
        "npc",
        "gs_nurse",
        tr(
          "血抽好送了，急件大概二十分鐘；備血要等配對，大概半小時。",
          "Labs drawn and sent. STAT is about twenty minutes; the crossmatch takes about half an hour.",
        ),
      );
      break;
    case "wu_ppi":
      S.ppiAt = S.t;
      post(
        ch,
        "npc",
        "gs_nurse",
        tr("Pantoprazole 80 打了。那……這樣就先觀察嗎？", "Pantoprazole 80 is in. So… we just watch her for now?"),
      );
      break;
    case "wu_fluid":
      S.fluidAt = S.t;
      post(ch, "npc", "gs_nurse", tr("LR 500 開始跑。", "LR 500 is running."));
      break;
    case "wu_ng":
      if (!wuHere()) return;
      S.ngAt = S.t;
      finding(
        tr("鼻胃管", "NG tube"),
        tr(
          "抽出約 60 mL 暗褐色、咖啡渣樣液體，夾雜少量鮮紅",
          "About 60 mL of dark brown, coffee-ground aspirate with a little bright red",
        ),
      );
      post(
        ch,
        "result",
        "gs_nurse",
        tr(
          "鼻胃管放好了，抽出 60 mL 左右，咖啡色的，有一點點鮮紅。",
          "NG tube’s in. Got about 60 mL back, coffee-colored, with a little bright red.",
        ),
      );
      break;
    case "li_scan":
      doScan("bedB", "gs_nurse");
      break;
    case "li_foley":
      fixFoley("bedB", "gs_nurse");
      break;
    case "li_lasix":
      S.lasixAt = S.t;
      post(
        ch,
        "npc",
        "gs_nurse",
        S.liFixedAt === null
          ? tr("Lasix 打了。尿袋……還是沒有尿耶。", "Lasix is in. The bag… still no urine, though.")
          : tr("Lasix 打了，尿一直出來。", "Lasix is in. Urine’s pouring out."),
      );
      break;
    case "li_fluid":
      S.liFluid = true;
      post(ch, "npc", "gs_nurse", tr("702 的 LR 500 開始跑。", "702’s LR 500 is running."));
      break;
    case "a_pump":
      fixPump("bedA", "gs_nurse");
      break;
    case "a_stilnox":
      S.stilnoxAt = S.t;
      post(
        ch,
        "npc",
        "gs_nurse",
        tr("Stilnox 給了。可是點滴機還在叫喔。", "Zolpidem’s given. But the IV pump is still alarming."),
      );
      break;
    case "a_calm":
      S.delirFix = "calm";
      clearWait("pg_701");
      post(
        ch,
        "npc",
        "gs_nurse",
        tr(
          "開了小燈，讓兒子坐在旁邊握著他的手，跟他說這裡是醫院、現在是半夜。他慢慢安靜下來了。",
          "I put the night light on, had his son sit by him and hold his hand, and told him he’s in the hospital and it’s the middle of the night. He’s slowly settling down.",
        ),
      );
      break;
    case "a_restrain":
      S.delirFix = "restrain";
      clearWait("pg_701");
      post(
        ch,
        "npc",
        "gs_nurse",
        tr(
          "綁好了。他一直掙扎、一直喊，兒子臉很臭。",
          "He’s tied down. He keeps fighting it and shouting, and his son looks furious.",
        ),
      );
      S.mood.zheng = (S.mood.zheng ?? 0) + 1;
      break;
  }
}

function nurseTick(t: number): void {
  const n = nurse();
  if (S.nAwayUntil !== null) {
    if (t < S.nAwayUntil) return;
    S.nAwayUntil = null;
    spawnAt("gs_nurse", "nurse", DOOR, SPOT.st);
    post("nurse", "npc", "gs_nurse", tr("我從 CT 回來了。", "I’m back from CT."));
  }
  if (S.bleedAt !== null && S.wuLoc === "ward") return;
  if (S.ncur && t >= S.ncur.until) {
    const id = S.ncur.id;
    S.ncur = null;
    finishTask(id);
    if (!S.nq.length) walkEnt(n, SPOT.st);
  }
  if (!S.ncur && S.nq.length) {
    const id = S.nq.shift();
    if (!id) return;
    S.ncur = { id, until: t + 1 + TASK[id].dur };
    walkEnt(n, SPOT[TASK[id].room]);
  }
}

/* ---------- 李伯伯 ---------- */

function doScan(ch: string, by: "clerk" | "gs_nurse"): void {
  if (S.liScan) {
    note(tr("剛剛照過了：膀胱約 480 mL。", "Already scanned: bladder about 480 mL."), ch);
    return;
  }
  S.liScan = true;
  const txt =
    S.liFixedAt !== null
      ? tr("膀胱約 40 mL，已經排空了。", "Bladder about 40 mL, already emptied.")
      : tr("膀胱容積約 480 mL。", "Bladder volume about 480 mL.");
  finding(tr("膀胱超音波", "Bladder scan"), txt);
  if (by === "clerk") {
    me(ch, tr("（膀胱超音波）", "(Bladder scan)"));
    post(ch, "result", null, txt);
    spend(2);
  } else post(ch, "result", "gs_nurse", tr(`膀胱掃描：${txt}`, `Bladder scan: ${txt}`));
}

function fixFoley(ch: string, by: "clerk" | "gs_nurse"): void {
  if (S.liFixedAt !== null) {
    note(tr("導尿管已經理順了，尿袋有在出尿。", "The Foley is straightened out and draining into the bag."), ch);
    return;
  }
  S.liFixedAt = S.t;
  clearWait("pg_702");
  const txt = tr(
    "順著導尿管摸下去，管子被李伯伯壓在右大腿底下，折成一個死角。理順、輕輕沖洗之後，尿一下子湧出來，十分鐘內流了 520 mL 的淡黃色尿。",
    "You trace the Foley down: Mr. Li is lying on it under his right thigh, kinked shut. Once you straighten it and flush it gently, urine gushes out, 520 mL of pale yellow urine within ten minutes.",
  );
  finding(
    tr("導尿管", "Foley"),
    tr("被壓在大腿下折到；理順後流出 520 mL", "Kinked under his thigh; 520 mL out after straightening"),
  );
  if (by === "clerk") {
    me(ch, tr("（順著導尿管檢查管路）", "(Traces the Foley tubing)"));
    post(ch, "narr", null, txt);
    post(
      ch,
      "npc",
      "li",
      tr("啊……輕鬆多了。剛剛下面脹得要命。", "Ahh… that’s so much better. It was killing me down there."),
    );
    spend(2);
  } else
    post(
      ch,
      "npc",
      "gs_nurse",
      tr(
        "導尿管被他壓在大腿下面折到了！理順以後尿一直出來，五百多。",
        "He was lying on the Foley, kinked under his thigh! Once I straightened it, urine just kept coming. Over 500.",
      ),
    );
}

/* ---------- 鄭伯伯 ---------- */

function fixPump(ch: string, by: "clerk" | "gs_nurse"): void {
  if (S.pumpFixedAt !== null) {
    note(tr("點滴機已經安靜了。", "The IV pump is already quiet."), ch);
    return;
  }
  S.pumpFixedAt = S.t;
  if (S.delirAt === null) clearWait("pg_701");
  finding(
    tr("701 點滴機", "701 IV pump"),
    tr(
      "下游阻塞警報：管路在手腕處折到；理順、重新啟動後安靜",
      "Downstream occlusion alarm: line kinked at the wrist; quiet after straightening and restarting",
    ),
  );
  const txt = tr(
    "螢幕閃著「下游阻塞 OCCLUSION」。管路在鄭伯伯的手腕那邊被他睡覺彎手壓折了。理順管路、用紙膠帶固定好、重新啟動。病房一下子安靜下來。",
    "The screen is flashing “DOWNSTREAM OCCLUSION.” Old Mr. Cheng bent his wrist in his sleep and kinked the line. You straighten it, tape it down with paper tape and restart the pump. The room goes quiet at once.",
  );
  if (by === "clerk") {
    me(ch, tr("（看點滴機）", "(Checks the IV pump)"));
    post(ch, "narr", null, txt);
    spend(2);
  } else
    post(
      ch,
      "npc",
      "gs_nurse",
      tr(
        "701 的點滴是手腕那邊折到，弄好了，終於不叫了。",
        "701’s IV was kinked at the wrist. Fixed it, and it finally stopped beeping.",
      ),
    );
  if (S.stilnoxAt === null)
    post(
      "bedA",
      "npc",
      "zheng",
      tr("……終於不叫了。歹勢，我剛剛口氣比較差。", "…It finally stopped. Sorry, I was pretty rude back there."),
    );
  S.mood.zheng = 0;
  if (S.zhOut) zhengBack();
}

/* ---------- orders that need a doctor ---------- */

function orderCta(ch: string): void {
  if (S.ctaAt !== null) {
    note(tr("CTA 已經排了。", "The CTA is already booked."), ch);
    return;
  }
  me(ch, tr("（打給 CT 室：703 吳太太，緊急腹部 CTA）", "(Calls CT: 703 Mrs. Wu, emergency CTA abdomen)"));
  if (!S.approved) {
    post(
      ch,
      "npc",
      "sys",
      tr(
        "CT 室技術員：「緊急 CTA 要有主治同意的醫囑喔，你們主治知道嗎？」",
        "CT tech: “An emergency CTA needs an order your attending signed off on. Does your attending know?”",
      ),
    );
    spend(1);
    return;
  }
  if (!wuHere() || S.bleedAt !== null) {
    note(tr("她現在不在病房或不適合推去 CT。", "She isn’t on the ward right now, or isn’t fit to go down to CT."), ch);
    return;
  }
  S.ctaAt = S.t;
  post(
    ch,
    "npc",
    "sys",
    tr(
      "CT 室技術員：「好，三分鐘後推下來，先確認腎功能和顯影劑過敏。」",
      "CT tech: “OK, bring her down in three minutes. Check her renal function and contrast allergies first.”",
    ),
  );
  spend(1);
}

function callIr(): void {
  openDialog("ir");
}

function activateIr(by: "clerk" | "vs"): void {
  if (S.irAt !== null) return;
  S.irAt = S.t;
  S.irReadyAt = S.t + IR_PREP;
  scheduleAngio();
  if (by === "vs")
    post(
      "vs",
      "narr",
      null,
      tr(
        "江主任直接打給放射科主治，介入團隊開始準備。",
        "Dr. Chiang calls the radiology attending directly. The IR team starts setting up.",
      ),
    );
}

/** Angio starts once IR is ready and the lesion is localized (CTA), or the attending is here to decide. */
function scheduleAngio(): void {
  if (S.irReadyAt === null || S.angioStarted !== null) return;
  if (S.ctaRead && S.ctaReadAt !== null) S.angioAt = Math.max(S.irReadyAt, S.ctaReadAt + 1, S.t + 1);
  else if (S.vsHere) S.angioAt = Math.max(S.irReadyAt, S.t + 4);
}

/* ---------- transport ---------- */

function bedOut(): void {
  S.bedAnim = { pts: TO_ELEVATOR, t0: performance.now(), dir: "out" };
  S.bedOut = true;
}

function bedIn(): void {
  S.bedAnim = { pts: [...TO_ELEVATOR].reverse(), t0: performance.now(), dir: "in" };
  S.bedOut = false;
}

function sendToCt(): void {
  S.wuLoc = "ct";
  if (S.dialog === "bedC") renderSide();
  closeZoomIf("bedC");
  bedOut();
  if (S.ncur) S.nq.unshift(S.ncur.id);
  S.ncur = null;
  S.nAwayUntil = S.t + 10;
  leaveBy(nurse(), DOOR);
  post(
    "bedC",
    "narr",
    null,
    tr(
      "欣怡和傳送阿伯把吳太太的床推出 703，往電梯走。",
      "Hsin-Yi and the old transport guy wheel Mrs. Wu’s bed out of 703 toward the elevator.",
    ),
  );
  post(
    "pager",
    "npc",
    "gs_nurse",
    tr("我陪她去 CT，病房先拜託你顧一下。", "I’m going down to CT with her. Please keep an eye on the ward for me."),
  );
}

function startAngio(): void {
  S.angioStarted = S.t;
  if (S.wuLoc === "ward") {
    closeZoomIf("bedC");
    bedOut();
    post(
      "bedC",
      "narr",
      null,
      tr(
        "介入放射科的人推著床來接吳太太，直接下去血管攝影室。",
        "The IR team comes with a bed for Mrs. Wu and takes her straight down to the angio suite.",
      ),
    );
  }
  S.wuLoc = "angio";
  eventBanner(tr("血管攝影", "Angiography"), tr("放射科介入治療室", "IR suite"));
  post(
    "ir",
    "npc",
    "ir",
    S.ctaRead
      ? tr("病人上檯了，照 CTA 的位置直接打 GDA。", "She’s on the table. Going straight for the GDA, per the CTA.")
      : tr(
          "病人上檯了，先打 celiac 跟 SMA 看哪裡在滲。",
          "She’s on the table. Shooting the celiac and SMA first to see where it’s leaking.",
        ),
  );
  S.endAt = S.t + 4;
  S.endCode = "A";
}

/* ---------- real images (photo mode only; drawn mode keeps the written reports) ---------- */

function imgMark(dlg: string, name: string, txt: string): void {
  if (S.findings.some((f) => f.name === name && f.text === txt)) note(txt, dlg);
  else {
    finding(name, txt);
    post(dlg, "narr", null, txt);
  }
}

function openCta(dlg: string): void {
  openZoom({
    dlg,
    title: tr("703 吳太太 CTA", "703 Mrs. Wu · CTA"),
    sub: tr("冠狀切面・動脈期", "Coronal · arterial phase"),
    draw: photoOnly,
    photo: {
      id: "ct_gda",
      overlay: (c, p) => sideMark(c, p),
      hot: [
        {
          x: 36,
          y: 38,
          w: 22,
          h: 24,
          label: tr("吻合處旁，手術夾附近亮白的一團", "Bright white blob by the anastomosis, near the clips"),
          fn: () =>
            imgMark(
              dlg,
              tr("CTA 影像", "CTA image"),
              tr(
                "GDA 殘端旁一團動脈期就亮起來的顯影劑：假性動脈瘤，旁邊是胰瘻留下的積液",
                "A blob of contrast next to the GDA stump that lights up in the arterial phase: a pseudoaneurysm, beside the collection left by the pancreatic fistula",
              ),
            ),
        },
        { x: 0, y: 6, w: 36, h: 56, label: tr("肝臟", "Liver"), fn: () => note(tr("肝臟。", "Liver."), dlg) },
        {
          x: 58,
          y: 16,
          w: 29,
          h: 32,
          label: tr("胃", "Stomach"),
          fn: () => note(tr("胃，裡面有一些液體。", "Stomach, with some fluid in it."), dlg),
        },
        {
          x: 6,
          y: 66,
          w: 70,
          h: 34,
          label: tr("下腹的腸子", "Lower bowel"),
          fn: () => note(tr("小腸，沒有明顯擴張。", "Small bowel, not obviously dilated."), dlg),
        },
      ],
    },
    hint: tr(
      "動脈期就亮的東西是血管；不該有的亮點就是在滲",
      "What lights up in the arterial phase is vessel; a bright spot where there shouldn’t be one is a leak",
    ),
  });
}

function openAngio(dlg: string, after: boolean): void {
  openZoom({
    dlg,
    title: after
      ? tr("血管攝影：栓塞後", "Angiogram: post-embolization")
      : tr("血管攝影：栓塞前", "Angiogram: pre-embolization"),
    sub: tr("從腹腔動脈幹打顯影劑", "Contrast injected from the celiac trunk"),
    draw: photoOnly,
    photo: after
      ? {
          id: "angio_coil",
          hot: [
            {
              x: 48,
              y: 64,
              w: 16,
              h: 16,
              label: tr("那一小團黑黑的金屬", "That little dark clump of metal"),
              fn: () =>
                imgMark(
                  dlg,
                  tr("血管攝影", "Angiogram"),
                  tr(
                    "線圈（coil）塞住了出血的那一段動脈，顯影劑不再往外滲",
                    "Coils plug the bleeding segment of artery; contrast no longer leaks out",
                  ),
                ),
            },
            {
              x: 78,
              y: 48,
              w: 58,
              h: 34,
              label: tr("上面的動脈分支", "Arterial branches above"),
              fn: () => note(tr("肝內的動脈分支還有顯影。", "The intrahepatic arterial branches still fill."), dlg),
            },
            {
              x: 0,
              y: 60,
              w: 44,
              h: 40,
              label: tr("白色細線", "Thin white lines"),
              fn: () => note(tr("導管和導絲。", "Catheter and guidewire."), dlg),
            },
          ],
        }
      : {
          id: "angio_gda",
          hot: [
            {
              x: 64,
              y: 64,
              w: 20,
              h: 18,
              label: tr("肝動脈下方鼓出來的地方", "The bulge below the hepatic artery"),
              fn: () =>
                imgMark(
                  dlg,
                  tr("血管攝影", "Angiogram"),
                  tr(
                    "胃十二指腸動脈殘端鼓出一個囊：假性動脈瘤",
                    "A sac bulging off the gastroduodenal artery stump: a pseudoaneurysm",
                  ),
                ),
            },
            {
              x: 46,
              y: 56,
              w: 48,
              h: 9,
              label: tr("橫過去的動脈", "The artery running across"),
              fn: () => note(tr("總肝動脈。", "Common hepatic artery."), dlg),
            },
            {
              x: 88,
              y: 36,
              w: 20,
              h: 30,
              label: tr("白色的圈", "The white loop"),
              fn: () => note(tr("導管，從腹腔動脈幹進來。", "The catheter, coming in through the celiac trunk."), dlg),
            },
            {
              x: 6,
              y: 12,
              w: 70,
              h: 42,
              label: tr("左上的分支", "Branches, upper left"),
              fn: () => note(tr("肝內的動脈分支。", "Intrahepatic arterial branches."), dlg),
            },
          ],
        },
    bar: after
      ? [C(tr("看栓塞前", "Before"), () => openAngio(dlg, false))]
      : S.t >= (S.angioStarted ?? Infinity) + 3
        ? [C(tr("看栓塞後", "After"), () => openAngio(dlg, true))]
        : [],
  });
}

/** Image buttons for the dialogs that can show them. */
function imageChoices(dlg: string): ChoiceItem[] {
  if (getImagery() !== "photo") return [];
  const out: ChoiceItem[] = [];
  if (S.ctaRead) out.push(C(tr("看 CTA 影像", "View CTA"), () => openCta(dlg)));
  const started = S.angioStarted;
  if (started !== null) out.push(C(tr("看血管攝影", "View angiogram"), () => openAngio(dlg, S.t >= started + 3)));
  return out.length ? [tr("影像", "Imaging"), ...out] : [];
}

function closeZoomIf(dlg: string): void {
  if (S.dialog === dlg) closeZoom();
}

/* ---------- the big bleed ---------- */

function bigBleed(): void {
  S.bleedAt = S.t;
  const v = wuVitals();
  S.vitalsSeen.push({ t: S.t, v });
  clearWait("pg_703");
  addWait({
    id: "bleed",
    text: tr("703 大出血！", "703 massive bleed!"),
    urgent: true,
    icon: tr("！", "!"),
    clearOn: "manual",
  });
  if (S.wuLoc === "ward") {
    eventBanner(
      tr("大出血", "Massive bleed"),
      tr(`703　BP ${v.sbp}/${v.dbp}　HR ${v.hr}`, `703 · BP ${v.sbp}/${v.dbp} · HR ${v.hr}`),
      true,
    );
    post(
      "bedC",
      "alert",
      "gs_nurse",
      tr(
        `醫師！703 吐血了！引流管整條都是鮮血，血壓 ${v.sbp}/${v.dbp}，心跳 ${v.hr}！快來！`,
        `Doctor! 703 is vomiting blood! The drain is full of fresh blood, BP ${v.sbp}/${v.dbp}, HR ${v.hr}! Get in here!`,
      ),
    );
    S.ncur = null;
    S.nq = [];
    walkEnt(nurse(), SPOT["703"]);
    finding(
      tr("大出血", "Massive bleed"),
      tr(
        `${clock()} 吐鮮血約 300 mL，引流管湧出鮮血，休克`,
        `${clock()} hematemesis of about 300 mL bright red blood, fresh blood pouring from the drain, in shock`,
      ),
    );
  } else {
    eventBanner(tr("大出血", "Massive bleed"), tr("吳太太在放射科", "Mrs. Wu is in radiology"), true);
    post(
      "pager",
      "alert",
      "gs_nurse",
      tr(
        `醫師！吳太太在 CT 室外面吐血了，血壓掉到 ${v.sbp}/${v.dbp}！放射科的人在幫忙壓、在叫人！`,
        `Doctor! Mrs. Wu is vomiting blood outside CT, BP down to ${v.sbp}/${v.dbp}! The radiology people are holding pressure and calling for help!`,
      ),
    );
    finding(
      tr("大出血", "Massive bleed"),
      tr(`${clock()} 於放射科吐血、休克`, `${clock()} hematemesis in radiology, in shock`),
    );
  }
  if (!S.vsAccepted) {
    S.vsAccepted = true;
    S.vsArriveAt = S.t + 12;
    startVsTrip("high", 12);
    post(
      "pager",
      "npc",
      "gs_nurse",
      tr("我直接打給江主任了，他說馬上來！", "I called Dr. Chiang myself. He says he’s coming right now!"),
    );
  } else if (S.vsArriveAt !== null && !S.vsHere && S.vsArriveAt - S.t > 6) S.vsArriveAt = S.t + 6;
  S.endAt = S.t + 7;
  S.endCode = resusScore() >= 3 ? "B" : null;
}

function resusScore(): number {
  let s = 0;
  if (S.iv2At !== null) s++;
  if ((S.tcReadyAt !== null && S.tcReadyAt <= S.t + 2) || S.oBlood) s++;
  if (S.mtp) s++;
  if (S.vsAcceptAt !== null && S.bleedAt !== null && S.vsAcceptAt < S.bleedAt) s++;
  if (S.irAt !== null) s++;
  if (S.monitor) s++;
  if (S.wuLoc !== "ward") s++;
  return s;
}

function crisis(id: "o" | "mtp" | "pressure" | "iv" | "vs" | "ir", ch: string): void {
  if (id === "vs" || id === "ir") {
    openDialog(id);
    return;
  }
  if (S.crisisActs.includes(id)) return;
  S.crisisActs.push(id);
  switch (id) {
    case "o":
      S.oBlood = true;
      me(ch, tr("（打給血庫：緊急發血 O 型 PRBC 4U）", "(Calls the blood bank: emergency release, 4U type O PRBC)"));
      post(
        ch,
        "npc",
        "bank",
        S.tcReadyAt !== null && S.tcReadyAt <= S.t
          ? tr(
              "配好的 4U 已經在送了，O 型也一起帶上去。",
              "The 4 crossmatched units are already on their way; we’ll send the O along too.",
            )
          : tr("O 型 Rh(+) 4U 緊急發血，五分鐘內送到。", "4U O Rh(+) emergency release, there in five minutes."),
      );
      break;
    case "mtp":
      S.mtp = true;
      me(ch, tr("（啟動大量輸血流程 MTP）", "(Activates the massive transfusion protocol)"));
      post(
        ch,
        "npc",
        "bank",
        tr(
          "MTP 啟動，PRBC、FFP、血小板 1:1:1，第一箱馬上出。",
          "MTP activated. PRBC, FFP, platelets 1:1:1. First cooler going out now.",
        ),
      );
      break;
    case "pressure":
      S.pressure = true;
      me(ch, tr("（輸液加壓袋，全開）", "(Pressure bags on, wide open)"));
      post(ch, "npc", "gs_nurse", tr("加壓袋套上了，全速。", "Pressure bags on, running wide open."));
      break;
    case "iv":
      if (S.iv2At !== null) return;
      S.iv2At = S.t;
      me(ch, tr("（再打兩條大管徑 IV）", "(Two more large-bore IVs)"));
      post(
        ch,
        "npc",
        "gs_nurse",
        tr("她血管都扁掉了……好，打上一條 16G、一條 18G。", "Her veins are all collapsed… OK, got a 16G and an 18G in."),
      );
      break;
  }
  spend(1);
  if (resusScore() >= 3) S.endCode = "B";
}

/* ---------- 701 delirium ---------- */

function delirium(): void {
  S.delirAt = S.t;
  page(
    "A4",
    "701",
    tr(
      "701 鄭伯伯在拔點滴、要爬下床，一直說他要回家！家屬壓不住！",
      "701 old Mr. Cheng is pulling at his IV and trying to climb out of bed, keeps saying he wants to go home! His son can’t hold him down!",
    ),
    tr("鄭伯伯躁動要爬下床", "Agitated, climbing out of bed"),
    8,
    true,
  );
  finding("701", tr("Zolpidem 後出現躁動、定向感混亂（譫妄）", "Agitated and disoriented after zolpidem (delirium)"));
}

/* ---------- endings ---------- */

function vsComment(): string {
  if (S.ppiAt !== null && !S.approved && S.rAdvice !== "escalate")
    return tr(
      "黑便加上 drain 有血，在 Whipple 有 fistula 的病人，你第一個要想到什麼？不是 ulcer。",
      "Melena plus blood in the drain, in a Whipple patient with a fistula. What’s the first thing you think of? Not an ulcer.",
    );
  if (S.stilnoxAt !== null)
    return tr(
      "還有，82 歲、上次術後就譫妄的人，你給他 Stilnox？",
      "And an 82-year-old who was delirious after surgery last time, you gave him zolpidem?",
    );
  if (S.lasixAt !== null && (S.liFixedAt === null || S.lasixAt < S.liFixedAt))
    return tr(
      "702 尿少，你 Foley 看都沒看就打 Lasix？",
      "702 has low urine output and you gave Lasix without even looking at the Foley?",
    );
  if (S.vsAcceptAt !== null && S.vsAcceptAt <= 20)
    return tr(
      "R 找不到就直接打給我。你做得對。",
      "Can’t reach the resident, you call me directly. You did the right thing.",
    );
  return tr(
    "Sentinel bleed 是給你的禮物。下一次出血不會這麼客氣。",
    "A sentinel bleed is a gift. The next bleed won’t be so polite.",
  );
}

function ward701Line(): string {
  if (S.delirFix === "restrain")
    return tr("701 的爸爸綁著約束帶，還在喊要回家。", "The dad in 701 is in restraints, still shouting to go home.");
  if (S.pumpFixedAt !== null)
    return tr(
      "701 的爸爸睡得很熟，兒子也趴在床邊睡著了。",
      "The dad in 701 is sound asleep, and his son has dozed off slumped over the bed.",
    );
  return tr("701 的點滴機還在嗶嗶叫。", "701’s IV pump is still beeping.");
}

function endA(): void {
  void playEnding(
    "A",
    tr("先一步", "One Step Ahead"),
    [
      {
        text: tr(
          "血管攝影室。螢幕上，胃十二指腸動脈殘端旁邊鼓出一顆 8 mm 的假性動脈瘤，顯影劑一陣一陣往外滲。",
          "The angio suite. On the screen, an 8 mm pseudoaneurysm bulges off the gastroduodenal artery stump, contrast leaking out in pulses.",
        ),
      },
      {
        who: "ir",
        text: tr("就是這個。Coil 放進去……好，沒有再滲了。", "That’s it. Coils going in… OK, no more leak."),
      },
      { who: "gs_vs", text: vsComment() },
      { who: "wu", text: tr("醫生……我是不是給大家添麻煩了？", "Doctor… did I cause everyone a lot of trouble?") },
      {
        who: "gs_nurse",
        text: tr(`學弟，你回病房看一下。${ward701Line()}`, `Hey, go back and check the ward. ${ward701Line()}`),
      },
    ],
    () =>
      tr(
        `吳太太在 ${clock(S.angioStarted ?? S.t)} 進血管攝影室，大出血之前就把假性動脈瘤栓塞了。${
          S.irAt !== null ? `你在 ${clock(S.irAt)} 啟動介入放射科。` : ""
        }`,
        `Mrs. Wu went into angio at ${clock(S.angioStarted ?? S.t)}, and the pseudoaneurysm was embolized before the big bleed.${
          S.irAt !== null ? ` You activated IR at ${clock(S.irAt)}.` : ""
        }`,
      ),
  );
}

function endB(): void {
  void playEnding(
    "B",
    tr("搶回來", "Pulled Back"),
    [
      {
        text: tr(
          "703 的地上一片紅。欣怡推著急救車，血庫的人小跑步把血送上來。",
          "The floor of 703 is red. Hsin-Yi wheels in the crash cart; someone from the blood bank jogs up with the blood.",
        ),
      },
      {
        who: "gs_vs",
        text: tr(
          "加壓輸血，直接推 angio，麻醉跟著。栓不住就開。",
          "Pressure-bag the blood, take her straight to angio, anesthesia comes along. If it won’t embolize, we open.",
        ),
      },
      {
        text: tr(
          "兩個小時後，栓塞完成。吳太太轉進外科加護病房，總共輸了 10 單位的血。",
          "Two hours later the embolization is done. Mrs. Wu goes to the surgical ICU, 10 units of blood in total.",
        ),
      },
      { who: "gs_vs", text: vsComment() },
      {
        who: "gs_nurse",
        text: tr("學弟，你的鞋子上都是血，去換一下吧。", "Your shoes are covered in blood, Doc. Go change."),
      },
    ],
    () =>
      tr(
        `吳太太在 ${clock(S.bleedAt ?? S.t)} 大出血，休克後搶救回來，轉加護病房。`,
        `Mrs. Wu had a massive bleed at ${clock(S.bleedAt ?? S.t)}, went into shock, was resuscitated and moved to the ICU.`,
      ),
  );
}

function endC(): void {
  void playEnding(
    "C",
    "Code Blue",
    [
      {
        text: tr(
          "「7A 703，Code Blue。」廣播響遍整層樓。",
          "“7A 703, Code Blue.” The overhead page rings across the whole floor.",
        ),
      },
      {
        text: tr(
          "血送到的時候，監視器上已經是一條很慢、很寬的波形。然後是一條直線。",
          "By the time the blood arrives, the monitor shows a slow, wide waveform. Then a flat line.",
        ),
      },
      {
        text: tr(
          "CPR 第八分鐘，心跳回來了。吳太太插著管、掛著三支升壓劑，推進加護病房，緊急血管攝影。",
          "Eight minutes into CPR, she gets a pulse back. Intubated, on three pressors, Mrs. Wu goes to the ICU and emergency angiography.",
        ),
      },
      { who: "gs_vs", text: vsComment() },
      { who: "gs_nurse", text: tr("……她一直說不要吵醫生。", "…She kept saying not to bother the doctors.") },
    ],
    () =>
      tr(
        `吳太太在 ${clock(S.bleedAt ?? S.t)} 大出血，心跳停止後急救恢復，轉加護病房，狀況危急。`,
        `Mrs. Wu had a massive bleed at ${clock(S.bleedAt ?? S.t)}, arrested, was resuscitated and moved to the ICU in critical condition.`,
      ),
  );
}

/* ---------- knowledge ---------- */

function known(): string {
  const out: string[] = [];
  out.push(
    tr(
      `公務機收到的 call：${
        S.pages
          .map(
            (p) =>
              `${clock(p.t)} ${p.room}「${p.text}」→ ${p.answeredAt !== null ? `${clock(p.answeredAt)} 到床邊` : "沒去"}`,
          )
          .join("；") || "無"
      }`,
      `Pages received: ${
        S.pages
          .map(
            (p) =>
              `${clock(p.t)} ${p.room} "${p.text}" → ${p.answeredAt !== null ? `at bedside ${clock(p.answeredAt)}` : "never went"}`,
          )
          .join("; ") || "none"
      }`,
    ),
  );
  out.push(
    tr(
      `703 生命徵象：${S.vitalsSeen.length ? S.vitalsSeen.map((x) => `${clock(x.t)} ${vtxt(x.v)}`).join("；") : "沒看"}`,
      `703 vitals: ${S.vitalsSeen.length ? S.vitalsSeen.map((x) => `${clock(x.t)} ${vtxt(x.v)}`).join("; ") : "not checked"}`,
    ),
  );
  out.push(
    tr(
      `看過 703 的引流管：${S.drainSeen !== null ? `是（${clock(S.drainSeen)}）` : "否"}`,
      `Looked at 703's drain: ${S.drainSeen !== null ? `yes (${clock(S.drainSeen)})` : "no"}`,
    ),
  );
  out.push(
    tr(
      `看過 703 的大便：${S.stoolSeen ? "是（黑色柏油便）" : "否"}`,
      `Looked at 703's stool: ${S.stoolSeen ? "yes (black tarry stool)" : "no"}`,
    ),
  );
  out.push(
    tr(
      `檢查發現：${S.findings.map((f) => `${clock(f.t)} ${f.name}：${f.text}`).join("；") || "無"}`,
      `Findings: ${S.findings.map((f) => `${clock(f.t)} ${f.name}: ${f.text}`).join("; ") || "none"}`,
    ),
  );
  out.push(
    tr(
      `吳太太說出的事：${[...S.wuRev].map((id) => WU_FACTS[id]).join("；") || "無"}`,
      `What Mrs. Wu has told: ${[...S.wuRev].map((id) => WU_FACTS[id]).join("; ") || "nothing"}`,
    ),
  );
  out.push(
    tr(
      `李伯伯說出的事：${[...S.liRev].map((id) => LI_FACTS[id]).join("；") || "無"}`,
      `What Mr. Li has told: ${[...S.liRev].map((id) => LI_FACTS[id]).join("; ") || "nothing"}`,
    ),
  );
  out.push(
    tr(
      `鄭先生說出的事：${[...S.zhRev].map((id) => ZH_FACTS[id]).join("；") || "無"}`,
      `What Mr. Cheng (701's son) has told: ${[...S.zhRev].map((id) => ZH_FACTS[id]).join("; ") || "nothing"}`,
    ),
  );
  const m: string[] = [];
  if (S.monitor) m.push(tr("703 接監視器", "703 on monitor"));
  if (S.iv2At !== null) m.push(tr(`${clock(S.iv2At)} 703 兩條大管徑 IV`, `${clock(S.iv2At)} 703 two large-bore IVs`));
  if (S.labsAt !== null)
    m.push(tr(`${clock(S.labsAt)} 703 抽血含備血`, `${clock(S.labsAt)} 703 labs incl. type & cross`));
  if (S.npo) m.push(tr("703 禁食", "703 NPO"));
  if (S.holdClexane) m.push(tr("703 停 Clexane", "703 enoxaparin held"));
  if (S.ppiAt !== null) m.push(`${clock(S.ppiAt)} 703 PPI`);
  if (S.fluidAt !== null) m.push(`${clock(S.fluidAt)} 703 LR 500`);
  if (S.ngAt !== null) m.push(tr("703 鼻胃管", "703 NG tube"));
  if (S.ctaAt !== null) m.push(tr(`${clock(S.ctaAt)} 排 CTA`, `${clock(S.ctaAt)} CTA booked`));
  if (S.irAt !== null) m.push(tr(`${clock(S.irAt)} 啟動介入放射科`, `${clock(S.irAt)} IR activated`));
  if (S.liScan) m.push(tr("702 膀胱超音波", "702 bladder scan"));
  if (S.liFixedAt !== null)
    m.push(tr(`${clock(S.liFixedAt)} 702 理順導尿管`, `${clock(S.liFixedAt)} 702 Foley straightened`));
  if (S.lasixAt !== null) m.push(`${clock(S.lasixAt)} 702 Lasix`);
  if (S.liFluid) m.push(tr("702 輸液", "702 IV fluids"));
  if (S.pumpFixedAt !== null)
    m.push(tr(`${clock(S.pumpFixedAt)} 701 處理點滴機`, `${clock(S.pumpFixedAt)} 701 IV pump fixed`));
  if (S.stilnoxAt !== null) m.push(tr(`${clock(S.stilnoxAt)} 701 Stilnox`, `${clock(S.stilnoxAt)} 701 zolpidem`));
  if (S.delirFix)
    m.push(
      tr(
        `701 譫妄處置：${S.delirFix === "calm" ? "安撫、重新定向" : "約束"}`,
        `701 delirium management: ${S.delirFix === "calm" ? "calming, reorientation" : "restraints"}`,
      ),
    );
  out.push(tr(`做過的處置：${m.join("、") || "無"}`, `Actions taken: ${m.join(", ") || "none"}`));
  out.push(
    tr(
      `聯絡：阿凱 ${S.rTalks} 次（建議：${{ none: "無", ppi: "先給 PPI", escalate: "直接找主任" }[S.rAdvice]}）；江主任${
        S.vsAcceptAt !== null ? ` ${clock(S.vsAcceptAt)} 同意並趕來` : S.vsTalked ? " 有打但沒說服" : "沒打"
      }；放射科${S.irTalked ? "有打" : "沒打"}`,
      `Contacts: Kai ${S.rTalks} time(s) (advice: ${{ none: "none", ppi: "give a PPI first", escalate: "go straight to the attending" }[S.rAdvice]}); Dr. Chiang: ${
        S.vsAcceptAt !== null
          ? `agreed at ${clock(S.vsAcceptAt)} and is coming in`
          : S.vsTalked
            ? "called but not convinced"
            : "not called"
      }; radiology: ${S.irTalked ? "called" : "not called"}`,
    ),
  );
  out.push(
    tr(
      `讀過的病歷：${[...S.emrRead].join("、") || "無"}；白板：${S.boardRead ? "看過" : "沒看"}`,
      `Charts read: ${[...S.emrRead].join(", ") || "none"}; whiteboard: ${S.boardRead ? "read" : "not read"}`,
    ),
  );
  return out.join("\n");
}

/* ---------- art: map ---------- */

const wuBedPaint = (c: Ctx, x: number, y: number): void => {
  DRAW.bed?.(c, x, y, {
    pt: true,
    hair: "#3a3038",
    skin: S.bleedAt !== null ? "#e9e2de" : "#ecd3c0",
    sheet: "#a9c8e8",
  });
  R(c, "#e7d8b0", x + 13, y + 22, 4, 7);
  R(c, S.bleedAt !== null ? "#b3263a" : S.rebled ? "#8a4a3a" : "#9a8a6a", x + 13, y + 25, 4, 4);
};

const noop: DrawFn = () => undefined;

const FURNITURE: Record<string, DrawFn> = {
  wubed: noop,
  scanner: noop,
  pump(c, px, py) {
    R(c, "#9aa0b2", px + 7, py + 1, 2, 15);
    R(c, "#6d7590", px + 4, py + 14, 8, 2);
    obox(c, "#e6e9f5", px + 3, py + 4, 10, 7);
    R(c, "#20263a", px + 5, py + 5, 6, 3);
    obox(c, "#e6f2f8", px + 9, py, 5, 4);
  },
  curtain(c, px, py, o) {
    for (let k = 0; k < (o.h ?? 1) * 16; k += 2) R(c, k % 4 ? "#b9cbe0" : "#cfdcec", px + 6, py + k, 4, 2);
    R(c, "#8a90a2", px + 5, py, 6, 1);
  },
  lockers(c, px, py, o) {
    for (let k = 0; k < (o.w ?? 1); k++) {
      const qx = px + k * 16;
      obox(c, "#8b97b3", qx + 1, py + 1, 14, 14);
      R(c, "#a5b0c8", qx + 2, py + 2, 12, 2);
      R(c, "#5a6278", qx + 11, py + 7, 2, 2);
    }
  },
  bigboard(c, px, py, o) {
    const w = (o.w ?? 1) * 16;
    obox(c, "#9aa0b2", px + 1, py + 1, w - 2, 13);
    R(c, "#ffffff", px + 2, py + 2, w - 4, 10);
    for (let k = 0; k < 4; k++) {
      R(c, "#3b5fa8", px + 4, py + 3 + k * 2, 6 + ((k * 5) % 9), 1);
      R(c, k === 2 ? "#c0394a" : "#3b5fa8", px + 16, py + 3 + k * 2, 8 + ((k * 3) % 7), 1);
    }
  },
};

/* ---------- art: close-ups ---------- */

const NIGHT_ROOM = {
  wall: "#bcc6dc",
  wall2: "#aeb9d2",
  base: "#8d97b3",
  floor: "#a8b3cb",
  floor2: "#9aa6c0",
};

function miniMonitor(c: Ctx, now: number): void {
  if (!S.monitor) {
    ptext(c, "--", 24, 15, "#3a4a46");
    ptext(c, "OFF", 22, 22, "#3a4a46");
    return;
  }
  const v = wuVitals();
  drawEcg(c, 10, 15, 26, 6, v.hr, now, "#3be37a");
  drawPleth(c, 10, 27, 26, 4, v.hr, now, "#4fd6ff");
  ptext(c, v.hr, 37, 9, "#3be37a");
  ptext(c, v.spo2, 37, 21, "#4fd6ff");
  if ((v.hr > 110 || v.sbp < 95) && Math.floor(now / 400) % 2) obox(c, "#ff3b4e", 47, 7, 3, 3);
}

function vitalsBoard(c: Ctx, lines: string[], col: string): void {
  lines.forEach((l, i) => ptext(c, l, 12, 9 + i * 7, col));
}

/** A JP drain bulb + collection bag hanging off the bed rail. */
function drainBag(c: Ctx, now: number, x: number, y: number, fluid: string, streak: string | null, fill: number): void {
  pline(c, x - 22, y - 8, x + 6, y + 4, "#e6e2c8", 1);
  obox(c, "#eef2f6", x, y, 24, 34);
  R(c, "#d6dde6", x + 1, y + 1, 22, 2);
  for (let k = 1; k < 6; k++) R(c, "#9aa4b8", x + 1, y + 34 - k * 6, 4, 1);
  const h = Math.round(clamp(fill, 0, 1) * 30);
  R(c, fluid, x + 2, y + 33 - h, 20, h);
  dither(c, x + 2, y + 33 - h, 20, Math.max(0, h), "#ffffff", 0.12);
  if (streak && h > 4) {
    const wob = Math.floor(now / 500) % 2;
    R(c, streak, x + 6 + wob, y + 33 - h, 3, Math.min(h, 12));
    R(c, streak, x + 13, y + 30 - Math.min(h, 8), 4, 3);
  }
}

function wuDrainColors(): { fluid: string; streak: string | null; fill: number } {
  if (S.bleedAt !== null) return { fluid: "#b3263a", streak: "#7a1022", fill: 1 };
  if (S.rebled) return { fluid: "#8f5a48", streak: "#c0283a", fill: 0.55 };
  return { fluid: "#a8987a", streak: "#c0283a", fill: 0.4 };
}

function wuScene(c: Ctx, now: number): void {
  const pale: Record<string, string> =
    S.bleedAt !== null ? { s: "#e9e2de", S: "#cfc6c2" } : S.rebled ? { s: "#efdcd2", S: "#d6bfb4" } : {};
  drawBedScene(c, now, {
    ...NIGHT_ROOM,
    pt: "wu",
    pale,
    monitor: miniMonitor,
    iv: true,
    ...(S.oBlood || S.mtp ? { iv2: "#a8323a" } : S.fluidAt !== null ? { iv2: "#f6e7a6" } : {}),
    pad: { size: 10, color: "#2a2228" },
    shiver: S.bleedAt !== null,
    extra(cc, n) {
      const d = wuDrainColors();
      drainBag(cc, n, 150, 66, d.fluid, d.streak, d.fill);
      if (S.ngAt !== null) pline(cc, 34, 42, 30, 70, "#e6e2c8", 1);
      if (S.bleedAt !== null) {
        obox(cc, "#c9ced9", 52, 76, 22, 8);
        R(cc, "#b3263a", 54, 77, 18, 4);
        dither(cc, 16, 58, 40, 6, "#b3263a", 0.3);
      }
      if (S.iv2At !== null) {
        R(cc, "#f4f4f6", 44, 52, 4, 2);
        R(cc, "#f4f4f6", 136, 52, 4, 2);
      }
    },
  });
}

function liScene(c: Ctx, now: number): void {
  drawBedScene(c, now, {
    ...NIGHT_ROOM,
    pt: "li",
    monitor: (cc) => vitalsBoard(cc, ["BP 118/68", "HR 86", "T 37.2"], "#7fe08a"),
    iv: true,
    ...(S.liFluid ? { iv2: "#f6e7a6" } : {}),
    extra(cc, n) {
      const full = S.liFixedAt !== null;
      pline(cc, 112, 58, 152, 74, "#e8d48a", 1);
      if (!full) {
        R(cc, "#e8d48a", 122, 56, 4, 2);
        R(cc, "#e8d48a", 124, 54, 2, 2);
      }
      drainBag(cc, n, 150, 66, "#e8c860", null, full ? 0.75 : 0.06);
      drainBag(cc, n, 120, 72, "#d89a8a", null, 0.25);
      if (!full) pell(cc, 98, 52, 7, 4, "#9fb8d8");
    },
  });
}

const OLD_ZHENG: Record<string, string> = {
  h: "#e2e2e8",
  H: "#f2f2f6",
  b: "#c8c8d0",
  s: "#e8c4a0",
  S: "#c99c78",
};

function bedAScene(c: Ctx, now: number): void {
  drawBedScene(c, now, {
    ...NIGHT_ROOM,
    pt: "li",
    pale: OLD_ZHENG,
    monitor: (cc) => vitalsBoard(cc, ["BP 142/80", "HR 78", "T 36.8"], "#7fe08a"),
    iv: true,
    ...(S.zhOut ? {} : { companion: "zheng" }),
    shiver: S.delirAt !== null && S.delirFix === null,
    extra(cc, n) {
      obox(cc, "#e6e9f5", 70, 20, 18, 14);
      R(cc, "#20263a", 72, 22, 14, 6);
      const alarm = S.pumpFixedAt === null;
      if (alarm) {
        if (Math.floor(n / 300) % 2) {
          R(cc, "#ff3b4e", 84, 30, 3, 3);
          ptext(cc, "OCCL", 73, 23, "#ff5a6a");
        }
        pline(cc, 40, 54, 46, 50, "#e6e2c8", 1);
        pline(cc, 46, 50, 44, 46, "#e6e2c8", 1);
      } else ptext(cc, "25", 74, 23, "#7fe08a");
      if (S.delirFix === "restrain") {
        R(cc, "#e6e2c8", 44, 54, 6, 3);
        R(cc, "#e6e2c8", 132, 54, 6, 3);
      }
    },
  });
}

function drainZoom(): ZoomCfg {
  return {
    title: tr("703 吳太太・引流管", "703 Mrs. Wu · Drain"),
    sub: tr("右上腹 JP drain（胰空腸吻合旁）", "RUQ JP drain (by the pancreaticojejunostomy)"),
    dlg: "bedC",
    draw(c, now) {
      R(c, "#c9d0e2", 0, 0, 192, 108);
      for (let x = 0; x < 192; x += 24) R(c, "#bcc4d8", x, 0, 1, 108);
      obox(c, "#8790a8", 0, 20, 70, 6);
      obox(c, "#ecd3c0", 0, 30, 62, 40);
      R(c, "#d9b8a0", 0, 64, 62, 6);
      obox(c, "#a9c8e8", 0, 26, 62, 9);
      R(c, "#86a8cc", 0, 32, 62, 3);
      pline(c, 8, 50, 26, 58, "#c9a08a", 1);
      for (let k = 0; k < 4; k++) R(c, "#8a5a4a", 10 + k * 4, 51 + k * 2, 1, 2);
      obox(c, "#ffffff", 30, 40, 18, 14);
      R(c, "#d8c8a0", 34, 44, 10, 6);
      const d = wuDrainColors();
      pline(c, 39, 47, 100, 30, "#e6e2c8", 2);
      if (d.streak) pline(c, 60, 41, 96, 31, d.streak, 1);
      obox(c, "#eef2f6", 96, 18, 64, 82);
      R(c, "#d6dde6", 98, 20, 60, 4);
      for (let k = 1; k < 8; k++) {
        R(c, "#7a8498", 98, 100 - k * 10, 8, 1);
        ptext(c, k * 50, 108, 98 - k * 10, "#7a8498");
      }
      const h = Math.round(d.fill * 76);
      R(c, d.fluid, 122, 98 - h, 34, h);
      dither(c, 122, 98 - h, 34, h, "#ffffff", 0.1);
      if (d.streak) {
        const wob = Math.floor(now / 500) % 3;
        R(c, d.streak, 128 + wob, 98 - h, 4, Math.min(h, 30));
        R(c, d.streak, 140, 98 - Math.min(h, 14), 8, 4);
        if (S.rebled || S.bleedAt !== null) pell(c, 145, 92, 5, 3, "#5a0c18");
      }
      R(c, "#9aa4b8", 160, 10, 2, 90);
    },
    hot: [
      {
        x: 120,
        y: 22,
        w: 38,
        h: 78,
        label: tr("引流液（顏色、量）", "Drain fluid (color, amount)"),
        fn: () => lookDrain("bedC"),
      },
      {
        x: 28,
        y: 38,
        w: 24,
        h: 18,
        label: tr("引流管出口", "Drain exit site"),
        fn: () =>
          exam(
            "bedC",
            tr("引流管出口", "Drain exit site"),
            tr("看引流管出口", "Checks the drain exit site"),
            tr(
              "出口周圍紗布乾的，皮膚沒有紅腫，管子固定良好。",
              "Gauze around the site is dry, no redness or swelling, the tube is well secured.",
            ),
          ),
      },
    ],
    onBack: () => openZoom({ dlg: "bedC", ...wuZoom() }),
  };
}

function lookDrain(ch: string): void {
  S.drainSeen = S.t;
  const txt =
    S.bleedAt !== null
      ? tr(
          "引流管整條鮮紅，袋子幾分鐘內就滿了，有大血塊。",
          "The whole drain line is bright red; the bag fills within minutes, with large clots.",
        )
      : S.rebled
        ? tr(
            `引流袋裡原本灰褐色混濁的胰液上面，多了一層鮮紅色，大約 ${drainBlood()} mL 是紅的，有小血塊。`,
            `On top of the cloudy gray-brown pancreatic fluid in the bag there is now a bright red layer, about ${drainBlood()} mL of it red, with small clots.`,
          )
        : tr(
            "引流袋裡是灰褐色、混濁、像洗碗水的液體（胰液），今晚約 120 mL；底下有一條鮮紅色的血，大約 30 mL，管子裡也看得到紅色。",
            "The bag holds gray-brown, cloudy fluid like dishwater (pancreatic juice), about 120 mL tonight; underneath is a streak of bright red blood, about 30 mL, and you can see red in the tubing too.",
          );
  finding(tr("703 引流管", "703 drain"), txt);
  me(ch, tr("（看引流袋）", "(Looks at the drain bag)"));
  post(ch, "narr", null, txt);
  spend(1);
}

function exam(ch: string, name: string, label: string, txt: string): void {
  if (S.findings.some((f) => f.name === name && f.text === txt)) {
    note(tr(`剛剛看過了：${txt}`, `Already checked: ${txt}`), ch);
    return;
  }
  me(ch, tr(`（${label}）`, `(${label})`));
  finding(name, txt);
  post(ch, "narr", null, txt);
  spend(1);
}

function wuAbd(): void {
  S.abdDone = true;
  exam(
    "bedC",
    tr("703 腹部", "703 abdomen"),
    tr("摸肚子、看傷口", "Palpates the belly, checks the wound"),
    S.bleedAt !== null
      ? tr(
          "肚子比剛剛脹，右上腹壓痛，傷口敷料滲血。",
          "The belly is more distended than before, RUQ tender, blood seeping through the wound dressing.",
        )
      : tr(
          "腹部軟，右上腹引流管附近輕微壓痛，沒有反彈痛；傷口乾淨。腸音稍微活躍。",
          "Abdomen soft, mild tenderness in the RUQ around the drain, no rebound; wound clean. Bowel sounds slightly hyperactive.",
        ),
  );
}

function wuStool(): void {
  S.stoolSeen = true;
  exam(
    "bedC",
    tr("703 大便", "703 stool"),
    tr("看欣怡留下來的尿布", "Checks the diaper Hsin-Yi kept"),
    tr(
      "尿布上一大攤黑色、黏稠、發亮的柏油便，有很重的腥臭味。",
      "A large pool of black, sticky, shiny tarry stool on the diaper, with a heavy, foul smell.",
    ),
  );
}

function wuZoom(): ZoomCfg {
  return {
    title: tr("703 吳太太", "703 Mrs. Wu"),
    sub: tr("點監視器、肚子、引流管、尿布……", "Tap the monitor, belly, drain, diaper…"),
    draw: wuScene,
    hot: (): Hotspot[] => [
      ...bedHots({
        ptLabel: tr("吳太太（跟她說話）", "Mrs. Wu (talk to her)"),
        onMonitor: openWuMonitor,
        bellyLabel: tr("肚子／傷口", "Belly / wound"),
        onBelly: wuAbd,
        padLabel: tr("尿布（剛剛的大便）", "Diaper (the stool from earlier)"),
        onPad: wuStool,
        onIv: () =>
          note(
            tr(
              `點滴：${[
                S.iv2At !== null ? "兩條 18G" : "左手一條 22G，維持輸液 60 mL/hr",
                S.fluidAt !== null ? "LR 500 快速滴注" : "",
                S.oBlood || S.mtp ? "PRBC 加壓輸注" : "",
              ]
                .filter(Boolean)
                .join("、")}`,
              `IV: ${[
                S.iv2At !== null ? "two 18Gs" : "one 22G in the left hand, maintenance fluids at 60 mL/hr",
                S.fluidAt !== null ? "LR 500 bolus" : "",
                S.oBlood || S.mtp ? "PRBC under pressure" : "",
              ]
                .filter(Boolean)
                .join(", ")}`,
            ),
            "bedC",
          ),
      }),
      {
        x: 146,
        y: 62,
        w: 30,
        h: 40,
        label: tr("引流管（JP drain）", "Drain (JP)"),
        fn: () => openZoom(drainZoom()),
      },
    ],
  };
}

function openWuMonitor(): void {
  if (!S.monitor) {
    note(
      tr(
        "她沒有接監視器。可以自己量，或請欣怡推監視器來接。",
        "She isn’t on a monitor. You can take vitals yourself, or ask Hsin-Yi to bring the monitor over.",
      ),
      "bedC",
    );
    return;
  }
  seeVitals("bedC");
  openMonitor(
    tr("703 吳太太・監視器", "703 Mrs. Wu · Monitor"),
    () => {
      const v = wuVitals();
      const last = S.vitalsSeen.at(-1);
      return {
        hr: v.hr,
        spo2: v.spo2,
        rr: v.rr,
        sbp: last ? last.v.sbp : null,
        dbp: last ? last.v.dbp : null,
        nibpT: last ? clock(last.t) : null,
        alarm: v.hr > 120 || v.sbp < 90,
      };
    },
    [C(tr("重新量 NIBP（1 分鐘）", "Repeat NIBP (1 min)"), () => seeVitals("bedC"))],
    () => {
      if (S.dialog === "bedC" && wuHere()) openZoom({ dlg: "bedC", ...wuZoom() });
    },
  );
}

function liZoom(): ZoomCfg {
  return {
    title: tr("702 李伯伯", "702 Mr. Li"),
    sub: tr("點肚子、尿袋、導尿管……", "Tap the belly, urine bag, Foley…"),
    draw: liScene,
    hot: (): Hotspot[] => [
      ...bedHots({
        ptLabel: tr("李伯伯（跟他說話）", "Mr. Li (talk to him)"),
        monLabel: tr("生命徵象紀錄", "Vitals chart"),
        onMonitor: () =>
          exam(
            "bedB",
            tr("702 生命徵象", "702 vitals"),
            tr("看生命徵象紀錄", "Reads the vitals chart"),
            tr(
              "BP 118/68，HR 86，體溫 37.2，SpO₂ 97%。四小時尿量 40 mL。昨晚 22:00 前每小時 50–60 mL。",
              "BP 118/68, HR 86, temp 37.2, SpO₂ 97%. Urine output 40 mL over four hours. Before 22:00 last night, 50–60 mL an hour.",
            ),
          ),
        bellyLabel: tr("下腹部（膀胱）", "Lower belly (bladder)"),
        onBelly: () =>
          exam(
            "bedB",
            tr("702 下腹", "702 lower abdomen"),
            tr("摸下腹部", "Palpates the lower belly"),
            S.liFixedAt !== null
              ? tr("恥骨上方軟了，不脹。", "Soft above the pubic bone now, not distended.")
              : tr(
                  "恥骨上方鼓鼓的、摸得到一個圓圓的東西，一壓李伯伯就皺眉說想尿。右上腹傷口敷料乾淨。",
                  "Bulging above the pubic bone; you can feel something round. When you press, Mr. Li winces and says he needs to pee. The RUQ wound dressing is clean.",
                ),
          ),
      }),
      {
        x: 108,
        y: 48,
        w: 34,
        h: 14,
        label: tr("導尿管路", "Foley tubing"),
        fn: () => fixFoley("bedB", "clerk"),
      },
      {
        x: 146,
        y: 62,
        w: 30,
        h: 40,
        label: tr("尿袋", "Urine bag"),
        fn: () =>
          exam(
            "bedB",
            tr("702 尿袋", "702 urine bag"),
            tr("看尿袋", "Checks the urine bag"),
            S.liFixedAt !== null
              ? tr("尿袋裡 560 mL 淡黃色的尿。", "560 mL of pale yellow urine in the bag.")
              : tr(
                  "尿袋幾乎是空的，只有底部一點點深黃色的尿。",
                  "The bag is nearly empty, just a little dark yellow urine at the bottom.",
                ),
          ),
      },
      {
        x: 116,
        y: 70,
        w: 26,
        h: 30,
        label: tr("肝切除引流管", "Hepatectomy drain"),
        fn: () =>
          exam(
            "bedB",
            tr("702 引流管", "702 drain"),
            tr("看肝切除的引流管", "Checks the hepatectomy drain"),
            tr(
              "淡紅色、清澈的血水樣引流液，今晚 80 mL，沒有膽汁色。",
              "Pale red, clear serosanguinous fluid, 80 mL tonight, no bile staining.",
            ),
          ),
      },
    ],
  };
}

function bedAZoom(): ZoomCfg {
  return {
    title: tr("701 鄭伯伯", "701 Mr. Cheng Sr."),
    sub: tr("點滴機、病人、兒子……", "IV pump, patient, son…"),
    draw: bedAScene,
    hot: (): Hotspot[] => [
      ...bedHots({
        ptLabel: tr("鄭伯伯（跟他說話）", "Mr. Cheng Sr. (talk to him)"),
        monLabel: tr("生命徵象紀錄", "Vitals chart"),
        onMonitor: () =>
          exam(
            "bedA",
            tr("701 生命徵象", "701 vitals"),
            tr("看生命徵象紀錄", "Reads the vitals chart"),
            tr(
              "BP 142/80，HR 78，體溫 36.8，SpO₂ 96%。都很穩定。",
              "BP 142/80, HR 78, temp 36.8, SpO₂ 96%. All stable.",
            ),
          ),
        bellyLabel: tr("肚子", "Belly"),
        onBelly: () =>
          exam(
            "bedA",
            tr("701 腹部", "701 abdomen"),
            tr("摸肚子", "Palpates the belly"),
            tr(
              "腹腔鏡傷口四個小洞都乾乾淨淨，腹部軟。",
              "All four laparoscopic port sites are clean and dry; abdomen soft.",
            ),
          ),
        ...(S.zhOut ? {} : { companion: "zheng", companionLabel: tr("鄭先生（跟他說話）", "Mr. Cheng (talk to him)") }),
      }),
      { x: 68, y: 18, w: 22, h: 18, label: tr("點滴機", "IV pump"), fn: () => fixPump("bedA", "clerk") },
      {
        x: 36,
        y: 44,
        w: 14,
        h: 14,
        label: tr("手腕的點滴管", "IV line at the wrist"),
        fn: () => fixPump("bedA", "clerk"),
      },
    ],
  };
}

/* ---------- art: pager close-up ---------- */

/** Pages that show "!" on the pager screen (matched against the page text, which is in the current locale). */
const PAGER_URGENT = tr(/吐血|爬下床|鮮紅|大出血/, /vomiting blood|climb(ing)? out of bed|bright red|massive bleed/i);
/** Pages that make a room's door light blink red. */
const DOOR_URGENT = tr(/鮮|扯|爬|吐/, /bright red|fresh blood|yank|climb|vomit/i);

function pagerZoom(): ZoomCfg {
  const rows = (): Page[] => S.pages.slice(-6);
  return {
    title: tr("公務機", "Pager"),
    sub: tr("最近的來電", "Recent pages"),
    draw(c, now) {
      R(c, "#2a2e3e", 0, 0, 192, 108);
      dither(c, 0, 0, 192, 108, "#343a4e", 0.3);
      obox(c, "#3a3f52", 52, 4, 88, 100);
      R(c, "#4a5068", 54, 6, 84, 4);
      obox(c, "#9fb89a", 58, 12, 76, 60);
      R(c, "#8ea889", 58, 12, 76, 2);
      rows().forEach((p, i) => {
        const y = 16 + i * 9;
        const col = p.answeredAt === null ? "#1b2a18" : "#5a7056";
        ptext(c, clock(p.t), 61, y, col);
        ptext(c, p.room, 91, y, col);
        if (p.answeredAt === null && Math.floor(now / 400) % 2) R(c, "#1b2a18", 108, y + 1, 3, 3);
        const urg = PAGER_URGENT.test(p.text);
        if (urg) ptext(c, "!", 116, y, col);
      });
      for (let k = 0; k < 9; k++) obox(c, "#5a6078", 62 + (k % 3) * 24, 76 + Math.floor(k / 3) * 9, 20, 7);
    },
    hot: () =>
      rows().map((p, i) => ({
        x: 58,
        y: 14 + i * 9,
        w: 76,
        h: 9,
        label: `${clock(p.t)} ${ROOM_NAME[p.room]}`,
        fn: () =>
          note(
            tr(
              `${clock(p.t)}　${p.text}${p.answeredAt !== null ? `（${clock(p.answeredAt)} 到床邊）` : "（還沒去）"}`,
              `${clock(p.t)} · ${p.text}${p.answeredAt !== null ? ` (at bedside ${clock(p.answeredAt)})` : " (not yet seen)"}`,
            ),
          ),
      })),
  };
}

/* ---------- LLM dialogs ---------- */

async function wuTalk(text: string): Promise<void> {
  const sp: Record<string, string> = { wu: tr("吳太太本人", "Mrs. Wu herself") };
  const n = nurse();
  if (!n.hidden && Math.abs(n.x - 16) + Math.abs(n.y - 3) <= 2)
    sp.gs_nurse = tr("欣怡（在床邊）", "Hsin-Yi (at the bedside)");
  await chat(text, {
    ch: "bedC",
    think: "wu",
    speakers: sp,
    body: tr(
      `你在醫學教育遊戲中扮演外科病房 703 床的「吳太太」${sp.gs_nurse ? "，以及在床邊的夜班護理師「欣怡」" : ""}。玩家是半夜幫忙接病房 call 的外科見習醫學生（clerk）。
吳太太：62 歲，退休國小老師，胰頭癌做 Whipple 手術後第 9 天，術後有胰液滲漏（引流管還在）。非常客氣、很怕麻煩別人，一直說「沒事啦，不要吵醫生，大家半夜都在睡」。被直接問到的事不會說謊，但會輕描淡寫，要具體問才會講細節。講話溫和，偶爾夾台語。
${sp.gs_nurse ? "欣怡：夜班護理師，忙但專業，只在被問到護理紀錄或處置時插話，不替 clerk 下診斷。她知道：01:50 吳太太按鈴說拉肚子，是黑便，尿布有留著；21:00 有打 Clexane。" : ""}
【隱藏真相，不能說出】胃十二指腸動脈殘端的假性動脈瘤正在少量出血（sentinel bleed），之後可能大出血。
【可揭露的事實】只有被問到相關問題才說，括號是 id：
${Object.entries(WU_FACTS)
  .map(([k, v]) => `- (${k}) ${v}`)
  .join("\n")}
dizzy 只有被問到頭暈、站起來、去廁所的情況才說。earlier 只有被問到之前有沒有看過紅紅的、什麼時候開始才說。
【狀態】${clock()}；${wuCondition()}。${S.bleedAt !== null ? "她正在大出血，只能講很短的話。" : ""}已揭露：${[...S.wuRev].join(",") || "無"}`,
      `In a medical education game you play "Mrs. Wu" in bed 703 on the surgical ward${sp.gs_nurse ? ', and also the night nurse "Hsin-Yi" at her bedside' : ""}. The player is a surgical clerk (medical student) covering the ward's pages in the middle of the night.
Mrs. Wu: 62, retired elementary school teacher, post-op day 9 after a Whipple for pancreatic head cancer, with a post-op pancreatic leak (the drain is still in). Extremely polite and terrified of being a bother; she keeps saying "I'm fine, really, don't wake the doctors, everyone's asleep at this hour." She won't lie about anything she's asked directly, but she plays things down and only gives details when asked specifically. She speaks gently, in plain, warm English; she may slip in the odd Taiwanese word, but her replies read as English.
${sp.gs_nurse ? "Hsin-Yi: the night nurse, busy but professional. She only chimes in when asked about nursing records or what's been done, and never makes the diagnosis for the clerk. She knows: at 01:50 Mrs. Wu rang the bell saying she had diarrhea; it was black stool, and the diaper has been kept; enoxaparin (Clexane) was given at 21:00." : ""}
[HIDDEN TRUTH, NEVER SAY IT] A pseudoaneurysm of the gastroduodenal artery stump is bleeding a little (a sentinel bleed), and a massive bleed may follow.
[FACTS SHE CAN REVEAL] Only when asked something related; the id is in parentheses:
${Object.entries(WU_FACTS)
  .map(([k, v]) => `- (${k}) ${v}`)
  .join("\n")}
Say dizzy only when asked about dizziness, standing up, or what happened in the bathroom. Say earlier only when asked whether she had seen anything red before, or when it started.
[STATE] ${clock()}; ${wuCondition()}.${S.bleedAt !== null ? " She is bleeding massively right now and can only manage very short phrases." : ""} Already revealed: ${[...S.wuRev].join(",") || "none"}`,
    ),
    extra: `"revealed":[]`,
    after(r) {
      for (const id of strList(r.revealed)) if (isWuFact(id)) S.wuRev.add(id);
    },
  });
}

async function liTalk(text: string): Promise<void> {
  await chat(text, {
    ch: "bedB",
    think: "li",
    speakers: { li: tr("李伯伯", "Mr. Li") },
    body: tr(
      `你在醫學教育遊戲中扮演外科病房 702 床的「李伯伯」，71 歲，B 肝帶原，肝癌做右後葉肝切除術後第 1 天。玩家是半夜來看他的外科見習醫學生（clerk）。
李伯伯有點重聽，講話台語夾國語（「啊」「歹勢」「足艱苦」），半睡半醒、有點煩躁，一直想坐起來。
【隱藏真相，不能說出】導尿管被他翻身時壓在大腿下折到，膀胱漲滿，所以尿袋沒有尿。
【可揭露的事實】被問到相關的才說，括號是 id：
${Object.entries(LI_FACTS)
  .map(([k, v]) => `- (${k}) ${v}`)
  .join("\n")}
full 只要被問到哪裡不舒服、肚子、尿尿就會講。不要說出診斷。
【狀態】${clock()}。${S.liFixedAt !== null ? "導尿管已經理順，尿出來了，他很舒服、想睡。" : S.liPulled ? "他剛剛扯到導尿管，很痛很生氣。" : "下腹很脹，很煩躁。"}`,
      `In a medical education game you play "Mr. Li" in bed 702 on the surgical ward: 71, hepatitis B carrier, post-op day 1 after a right posterior sectionectomy (liver resection) for liver cancer. The player is a surgical clerk (medical student) coming to see him in the middle of the night.
Mr. Li is a bit hard of hearing and talks like a folksy old Taiwanese grandpa, in short, plain English with grunts and complaints ("Ah…", "sorry, sorry", "it's awful"); he's half asleep, a bit agitated, and keeps trying to sit up. He may drop the odd romanized Taiwanese word, but his replies read as English.
[HIDDEN TRUTH, NEVER SAY IT] When he rolled over, his Foley got pinned and kinked under his thigh; his bladder is full, which is why the bag has no urine.
[FACTS HE CAN REVEAL] Only when asked something related; the id is in parentheses:
${Object.entries(LI_FACTS)
  .map(([k, v]) => `- (${k}) ${v}`)
  .join("\n")}
He says full as soon as he's asked where it's uncomfortable, about his belly, or about peeing. Never state the diagnosis.
[STATE] ${clock()}. ${S.liFixedAt !== null ? "The Foley has been straightened and the urine has come out; he feels much better and wants to sleep." : S.liPulled ? "He just yanked on his Foley; he's in a lot of pain and angry." : "His lower belly is very full and he's agitated."}`,
    ),
    extra: `"revealed":[]`,
    after(r) {
      for (const id of strList(r.revealed)) if (isLiFact(id)) S.liRev.add(id);
    },
  });
}

async function zhengTalk(text: string): Promise<void> {
  const fixed = S.pumpFixedAt !== null;
  await chat(text, {
    ch: "bedA",
    think: "zheng",
    speakers: { zheng: tr("鄭先生", "Mr. Cheng") },
    body: tr(
      `你在醫學教育遊戲中扮演外科病房 701 床病人的兒子「鄭先生」，48 歲，做業務，明天一早還要上班，陪 82 歲的爸爸住院（腹腔鏡膽囊切除術後第 1 天）。玩家是半夜來處理的外科見習醫學生（clerk）。
鄭先生很累、很火大：爸爸睡不著，他按了好幾次鈴都沒人來。他一開始要求「給我爸安眠藥」，講話衝，會說要投訴。但他不是壞人，問題解決了就會道歉。
${fixed ? "現在點滴機已經不叫了，他氣消了，有點不好意思。" : "點滴機一直在叫，所以他越講越氣。"}
【他知道的事】被問到相關的才說，括號是 id：
${Object.entries(ZH_FACTS)
  .map(([k, v]) => `- (${k}) ${v}`)
  .join("\n")}
beep：只要 clerk 問爸爸為什麼睡不著，他就會抱怨點滴機一直叫。delirium：被問到爸爸之前的狀況、有沒有亂講話、之前吃安眠藥的情形才說。saw703：只要 clerk 問到其他病人、走廊、或聊到別的事，他會提到；或 clerk 很有禮貌地處理好他爸爸的事之後，他會主動順口提一句。
不要說出任何醫學診斷。
【狀態】${clock()}。${S.delirAt !== null && S.delirFix === null ? "爸爸吃了安眠藥後在亂講話、要爬下床，他嚇壞了。" : ""}已揭露：${[...S.zhRev].join(",") || "無"}`,
      `In a medical education game you play "Mr. Cheng", the son of the patient in bed 701 on the surgical ward: 48, a salesman who has to be at work first thing tomorrow, staying with his 82-year-old father (post-op day 1 after a laparoscopic cholecystectomy). The player is a surgical clerk (medical student) coming to deal with it in the middle of the night.
Mr. Cheng is exhausted and furious: his dad can't sleep, and he's pressed the call bell several times with nobody coming. At first he demands "give my dad a sleeping pill," talks aggressively, and threatens to file a complaint. But he's not a bad guy; once the problem is fixed he apologizes.
${fixed ? "The IV pump has stopped beeping now; his anger has faded and he's a little embarrassed." : "The IV pump keeps beeping, so the more he talks the angrier he gets."}
[WHAT HE KNOWS] Only when asked something related; the id is in parentheses:
${Object.entries(ZH_FACTS)
  .map(([k, v]) => `- (${k}) ${v}`)
  .join("\n")}
beep: as soon as the clerk asks why his dad can't sleep, he complains that the IV pump keeps beeping. delirium: only when asked about his dad's condition before, whether he's been talking nonsense, or how he did on sleeping pills before. saw703: he mentions it whenever the clerk asks about other patients or the hallway, or the conversation turns to anything else; or, after the clerk has politely sorted out his dad's problem, he brings it up in passing on his own.
Never state any medical diagnosis.
[STATE] ${clock()}.${S.delirAt !== null && S.delirFix === null ? " After the sleeping pill his dad is talking nonsense and trying to climb out of bed; he's terrified." : ""} Already revealed: ${[...S.zhRev].join(",") || "none"}`,
    ),
    extra: `"revealed":[]`,
    after(r) {
      for (const id of strList(r.revealed)) if (isZhFact(id)) S.zhRev.add(id);
    },
  });
}

async function nurseTalk(text: string): Promise<void> {
  await chat(text, {
    ch: "nurse",
    think: "gs_nurse",
    speakers: { gs_nurse: tr("欣怡", "Hsin-Yi") },
    cost: 1,
    body: tr(
      `你在醫學教育遊戲中扮演外科病房夜班護理師「欣怡」，26 歲，夜班三年，今晚一個人顧 12 床，很忙、講話快、中英夾雜（「Hb 多少」「要不要 call R？」）。照醫師口頭醫囑做事，不替 clerk 下診斷；明顯有問題的醫囑會說「醫師你確定嗎？」。緊急 CTA、找放射科、叫主治這種事她會說「這要醫師打喔，電話在護理站」。
【她知道的】
- 703 吳太太：Whipple POD9，POD5 起胰液滲漏（drain amylase 很高），POD7 CT 有吻合處旁積液，抗生素中。01:50 她按鈴說拉肚子，是黑便，尿布有留著；引流管好像有一點紅。21:00 有打 Clexane 40 mg。她只有一條 22G。
- 702 李伯伯：肝切除 POD1，四小時尿 40 mL。她沒時間去看導尿管。
- 701 鄭伯伯：82 歲，膽囊切除 POD1，家屬一直按鈴要安眠藥。點滴機好像一直在叫，她還沒空過去。
- R3 阿凱在刀房開急刀（急診上來的嵌頓疝氣阿嬤），電話很難接。
【clerk 目前做過的事】
${known()}
【可執行的醫囑 id】${Object.entries(TASK)
        .map(([k, v]) => `${k}（${v.label}）`)
        .join("；")}；npo（703 禁食）；hold_clexane（703 停 Clexane）
clerk 明確要求的醫囑，把 id 放進 orders。不要編造還沒出來的結果。`,
      `In a medical education game you play "Hsin-Yi", the night nurse on the surgical ward: 26, three years of nights, alone with 12 beds tonight. Very busy, talks fast, in clipped clinical shorthand ("What's the Hb?", "Want me to call the resident?"). She carries out the doctors' verbal orders and never makes the diagnosis for the clerk; for an order that's clearly off she says "Doctor, are you sure?". For things like an emergency CTA, calling radiology, or calling the attending, she says "A doctor has to make that call. The phone's at the nurses' station."
[WHAT SHE KNOWS]
- 703 Mrs. Wu: Whipple POD9, pancreatic leak since POD5 (drain amylase very high), POD7 CT showed a collection next to the anastomosis, on antibiotics. At 01:50 she rang saying she had diarrhea; it was black stool, and the diaper has been kept; the drain looks a little red. Enoxaparin (Clexane) 40 mg given at 21:00. She has only one 22G IV.
- 702 Mr. Li: liver resection POD1, 40 mL of urine in four hours. She hasn't had time to check his Foley.
- 701 old Mr. Cheng: 82, cholecystectomy POD1; the family keeps ringing the bell asking for a sleeping pill. The IV pump seems to have been alarming nonstop; she hasn't had time to go over.
- The R3, Kai, is in the OR doing an emergency case (an elderly woman from the ER with an incarcerated hernia) and can barely take calls.
[WHAT THE CLERK HAS DONE SO FAR]
${known()}
[ORDER IDS SHE CAN CARRY OUT] ${Object.entries(TASK)
        .map(([k, v]) => `${k} (${v.label})`)
        .join("; ")}; npo (703 NPO); hold_clexane (703 hold enoxaparin)
Put the id of every order the clerk explicitly asks for into orders. Never make up results that aren't back yet.`,
    ),
    extra: `"orders":[]`,
    after(r) {
      for (const raw of strList(r.orders)) {
        const cur = S.dialog;
        if (raw === "npo") setNpo("nurse");
        else if (raw === "hold_clexane") setClexane("nurse");
        else if (isTask(raw)) nurseDo(raw, "nurse");
        if (S.dialog !== cur) {
          S.dialog = cur;
          renderSide();
        }
      }
    },
  });
}

function setNpo(ch: string): void {
  if (S.npo) return;
  S.npo = true;
  me(ch, tr("（703 禁食）", "(703 NPO)"));
  post(ch, "npc", "gs_nurse", tr("好，703 掛禁食牌，早餐取消。", "OK, NPO sign up on 703, breakfast canceled."));
}

function setClexane(ch: string): void {
  if (S.holdClexane) return;
  S.holdClexane = true;
  me(ch, tr("（703 停 Clexane）", "(703 hold enoxaparin)"));
  post(
    ch,
    "npc",
    "gs_nurse",
    tr(
      "Clexane 停掉。她昨晚九點打過一次喔。",
      "Enoxaparin on hold. Heads up, she already got a dose at nine last night.",
    ),
  );
}

async function rTalk(text: string): Promise<void> {
  S.rTalks++;
  await chat(text, {
    ch: "r",
    think: "circ",
    speakers: tr(
      { circ: "佩佩（刀房流動護理師，幫忙拿電話）", gs_r: "阿凱（R3，正在開刀）" },
      { circ: "Pei-Pei (OR circulating nurse, holding the phone)", gs_r: "Kai (R3, operating)" },
    ),
    cost: 2,
    body: tr(
      `你在醫學教育遊戲中扮演刀房裡的兩個人：流動護理師「佩佩」幫正在開刀的 R3「阿凱」拿著電話貼在他耳邊。阿凱在開一台急診上來的嵌頓疝氣、小腸缺血的急刀，手上在切腸子，很累、很急，講話極短。玩家是病房幫忙接 call 的外科見習醫學生（clerk）。
阿凱的反應取決於 clerk 講得清不清楚：
- clerk 講得含糊（例如只說「703 拉黑便」沒提 Whipple、fistula、drain 有血、心跳），阿凱會隨口說「應該 stress ulcer 吧，先給 PPI，抽個 CBC，早上再看」。
- clerk 講出 Whipple 術後、胰液滲漏、drain 有鮮血或黑便、心跳快，阿凱會立刻警覺：「那是 sentinel bleed！不要等我，直接打給江主任，現在！兩條大 IV、備血、禁食。」
- 701 的安眠藥、702 的尿少這種事，他會說「你自己先去看，看完再說」，702 會提醒「先看 Foley 有沒有折到」。
佩佩只有在阿凱講不清楚時補一句，或說「他在縫，你講快一點」。
【advice】ppi：阿凱建議先 PPI 觀察；escalate：阿凱叫 clerk 直接找主任；none：其他。`,
      `In a medical education game you play two people in the OR: the circulating nurse "Pei-Pei" is holding the phone to the ear of the R3 "Kai", who is scrubbed in. Kai is doing an emergency case from the ER, an incarcerated hernia with ischemic small bowel, and is resecting bowel right now; he's exhausted and rushed and talks in extremely short bursts. The player is a surgical clerk (medical student) covering the ward's pages.
Kai's reaction depends on how clearly the clerk presents:
- If the clerk is vague (e.g. only says "703 had black stool" without mentioning the Whipple, the fistula, blood in the drain, or the heart rate), Kai says offhandedly: "Probably a stress ulcer. Give a PPI, draw a CBC, we'll look in the morning."
- If the clerk mentions post-Whipple, the pancreatic leak, fresh blood in the drain or melena, and a fast heart rate, Kai is instantly alarmed: "That's a sentinel bleed! Don't wait for me, call Dr. Chiang directly, now! Two large-bore IVs, type and cross, NPO."
- For things like 701's sleeping pill or 702's low urine output, he says "Go look yourself first, then call me"; for 702 he reminds them "Check whether the Foley is kinked first."
Pei-Pei only adds a line when Kai isn't clear, or says "He's suturing, talk faster."
[advice] ppi: Kai advises a PPI and watching; escalate: Kai tells the clerk to go straight to the attending; none: anything else.`,
    ),
    extra: `"advice":"none"`,
    after(r) {
      if (r.advice === "escalate") S.rAdvice = "escalate";
      else if (r.advice === "ppi" && S.rAdvice === "none") S.rAdvice = "ppi";
    },
  });
}

async function vsTalk(text: string, incoming?: unknown): Promise<void> {
  S.vsTalked = true;
  const v = wuVitals();
  const latest =
    incoming === "cb"
      ? tr(
          "（這通是你在開車趕來醫院的路上打給 clerk 的，問 703 最新狀況：心跳血壓、引流量、IV、備血、放射科排好了沒。開口直接問。action 用 continue。）",
          "(You are calling the clerk from the car on your way in, to get 703's latest status: heart rate and BP, drain output, IVs, blood ready, whether radiology is set up. Open by asking directly. Use action continue.)",
        )
      : undefined;
  await chat(
    text,
    {
      ch: "vs",
      think: "gs_vs",
      tier: "default",
      speakers: { gs_vs: tr("江主任", "Dr. Chiang") },
      body: tr(
        `你在醫學教育遊戲中扮演一般外科（肝膽胰）主治「江主任」，${S.vsHere ? "人已經到病房了" : S.trip ? "正在開車趕來醫院" : "半夜在家被電話吵醒"}。話很少，要學生一句話講重點，聽到廢話會打斷（「重點。」「所以呢？」）。中英夾雜（「drain 什麼顏色？」「HR 多少？」「CTA 排了沒？」）。學生講得好就簡短說「好」並交代下一步。不直接說出答案，用問題逼學生想。
【真相】703 吳太太 62 歲，Whipple 術後第 9 天，POD5 起 grade B 胰液滲漏，POD7 CT 有積液。今晚 01:50 黑便一次、引流管少量鮮血、心跳 100 出頭：胃十二指腸動脈殘端假性動脈瘤的 sentinel bleed，之後會大出血。正確處置：兩條大 IV、抽血備血、禁食、停 Clexane、緊急 CTA、介入放射科栓塞，你要親自過來。給 PPI 觀察是錯的。
【clerk 實際知道與做過的】（講出以外的數據就追問「哪來的？」）
${known()}
【現況】${clock()}；吳太太實際 ${vtxt(v)}（clerk 不一定知道）；她現在在${{ ward: "病房", ct: "CT 室", angioWait: "放射科等血管攝影", angio: "血管攝影室" }[S.wuLoc]}。${S.bleedAt !== null ? "她已經大出血了。" : ""}
【action】accept_plan：clerk 講出 Whipple 術後／胰液滲漏，加上引流管有血或黑便，並提到心跳或血壓或懷疑出血。你判斷是 sentinel bleed：交代兩條大 IV、備血、禁食、停 Clexane、「CTA 現在排，你打給放射科值班 R 說我同意，我打給他們主治」，然後說你現在過去。
hang_up：只是為了 701 安眠藥或其他小事打給你，或完全沒看病人就打來，你冷冷叫他先去看病人。
continue：其他情況，包括已經同意過。${S.vsAccepted ? "你已經同意過、正在處理，不需要再 accept_plan，用 continue。" : ""}
【urgency】只在 accept_plan 時填：依 clerk「傳達出來」的急迫與清楚程度。high：講出數字（HR、drain 鮮血量）並明確請你馬上來；medium：重點有但不夠急；low：含糊、輕描淡寫。`,
        `In a medical education game you play "Dr. Chiang", the general surgery (HPB) attending, ${S.vsHere ? "who has already arrived on the ward" : S.trip ? "who is driving in to the hospital right now" : "woken at home by the phone in the middle of the night"}. You say very little and want the student to give you the point in one sentence; you cut off waffle ("The point." "And?"). Clipped clinical shorthand ("What color is the drain?" "HR?" "CTA booked?"). When the student presents well, you just say "OK" and give the next step. You never hand over the answer; you ask questions that force the student to think.
[TRUTH] 703 Mrs. Wu, 62, post-op day 9 after a Whipple, grade B pancreatic leak since POD5, collection on POD7 CT. Tonight at 01:50: one episode of melena, a little fresh blood in the drain, HR just over 100: a sentinel bleed from a pseudoaneurysm of the gastroduodenal artery stump, and a massive bleed will follow. Correct management: two large-bore IVs, labs with type and cross, NPO, hold enoxaparin (Clexane), emergency CTA, IR embolization, and you need to come in yourself. Giving a PPI and watching is wrong.
[WHAT THE CLERK ACTUALLY KNOWS AND HAS DONE] (if they quote numbers beyond this, press them: "Where's that from?")
${known()}
[CURRENT] ${clock()}; Mrs. Wu's actual vitals ${vtxt(v)} (the clerk may not know them); she is currently ${{ ward: "on the ward", ct: "in CT", angioWait: "in radiology waiting for angiography", angio: "in the angio suite" }[S.wuLoc]}.${S.bleedAt !== null ? " She has already had the massive bleed." : ""}
[action] accept_plan: the clerk states post-Whipple / pancreatic leak, plus blood in the drain or melena, and mentions the heart rate or BP or suspected bleeding. You judge it a sentinel bleed: order two large-bore IVs, type and cross, NPO, hold enoxaparin, and "Book the CTA now. Call the radiology resident on call and tell them I've approved it; I'll call their attending," then say you're coming in now.
hang_up: they called you just for 701's sleeping pill or some other minor thing, or called without having seen the patient at all; you coldly tell them to go see the patient first.
continue: anything else, including when you've already agreed.${S.vsAccepted ? " You have already agreed and are handling it; no need for accept_plan again, use continue." : ""}
[urgency] Fill in only with accept_plan, based on how urgent and clear the clerk actually *sounded*. high: gives numbers (HR, amount of fresh blood in the drain) and clearly asks you to come now; medium: has the key points but not urgent enough; low: vague, plays it down.`,
      ),
      extra: `"action":"continue","urgency":"medium"`,
      after(r) {
        if (r.action === "hang_up") {
          post("vs", "narr", null, tr("江主任掛斷了電話。", "Dr. Chiang hangs up."));
          S.mood.gs_vs = (S.mood.gs_vs ?? 0) + 1;
        }
        if (r.action === "accept_plan" && !S.vsAccepted) acceptVs(isTripUrgency(r.urgency) ? r.urgency : "medium");
      },
      cost: incoming ? 0 : 2,
    },
    latest,
  );
}

function acceptVs(urg: TripUrgency): void {
  S.approved = true;
  S.vsAccepted = true;
  S.vsAcceptAt = S.t;
  clearWait("callvs");
  if (S.vsHere) return;
  const eta = { high: 12, medium: 18, low: 26 }[urg];
  S.vsArriveAt = S.t + eta;
  startVsTrip(urg, eta);
  eventBanner(tr("江主任同意", "Dr. Chiang agrees"), tr("可以排 CTA、找放射科", "You can book the CTA and call IR"));
  post(
    "vs",
    "narr",
    null,
    tr("江主任掛了電話。你聽到背景有鑰匙的聲音。", "Dr. Chiang hangs up. You hear keys jangling in the background."),
  );
}

async function irTalk(text: string): Promise<void> {
  S.irTalked = true;
  await chat(text, {
    ch: "ir",
    think: "ir",
    speakers: { ir: tr("宗翰", "Tsung-Han") },
    cost: 2,
    body: tr(
      `你在醫學教育遊戲中扮演放射科（介入放射）值班住院醫師「宗翰」，R2，半夜被電話吵醒但很專業。玩家是外科病房幫忙接 call 的見習醫學生（clerk），打來要做緊急 CTA 或血管攝影栓塞。
宗翰會問：適應症（什麼手術、第幾天、出血的證據）、生命徵象、腎功能與凝血、有沒有顯影劑過敏、主治知不知道。
【規則】主治江主任${S.approved ? "已經同意，而且已經打給你們主治了" : "還沒同意（你不知道外科主治的意見）"}。
- 主治已同意，且 clerk 講出是 Whipple 術後出血或懷疑假性動脈瘤：action 用 activate，說「好，我 call 學長跟放射師，大概十幾分鐘可以開始，CTA 如果先做完更好」。
- 主治還沒同意：說「這種要你們主治跟我們主治講喔，你先找你們主治」，action 用 need_vs。
- 其他：continue。
${S.irAt !== null ? "你已經在準備了，action 用 continue，可以回報準備進度。" : ""}${S.ctaRead ? "CTA 你看過了：GDA 殘端 8 mm 假性動脈瘤，有顯影劑外滲。" : ""}`,
      `In a medical education game you play "Tsung-Han", the on-call radiology (interventional radiology) resident, an R2: woken by the phone in the middle of the night but very professional. The player is a clerk (medical student) covering pages on the surgical ward, calling to request an emergency CTA or angiographic embolization.
Tsung-Han asks about: the indication (what operation, what post-op day, evidence of bleeding), vitals, renal function and coags, contrast allergy, and whether the attending knows.
[RULES] The attending, Dr. Chiang, ${S.approved ? "has already approved it and has already called your attending" : "has not approved it yet (you don't know the surgical attending's view)"}.
- Attending has approved, and the clerk says it's a post-Whipple bleed or a suspected pseudoaneurysm: use action activate and say "OK, I'll call my senior and the rad tech. We can start in ten-something minutes; even better if the CTA gets done first."
- Attending has not approved yet: say "For this your attending has to talk to our attending. Get hold of your attending first," and use action need_vs.
- Otherwise: continue.
${S.irAt !== null ? "You are already setting up; use action continue, and you can report how the prep is going." : ""}${S.ctaRead ? "You have seen the CTA: an 8 mm pseudoaneurysm off the GDA stump with active contrast extravasation." : ""}`,
    ),
    extra: `"action":"continue"`,
    after(r) {
      if (r.action === "activate" && S.approved && S.irAt === null) {
        activateIr("clerk");
        post(
          "ir",
          "narr",
          null,
          tr(
            `介入放射科開始準備，預計 ${clock(S.irReadyAt ?? S.t)} 可以開始。`,
            `IR starts setting up; they expect to start at ${clock(S.irReadyAt ?? S.t)}.`,
          ),
        );
      }
    },
  });
}

/* ---------- dialogs ---------- */

function nurseOrderChoices(room: Room | "all"): ChoiceItem[] {
  const ch = room === "all" ? "nurse" : ROOM_DLG[room];
  const t = (id: TaskId, label?: string): ChoiceItem =>
    C(label ?? TASK[id].label.slice(4), () => nurseDo(id, ch), taskDone(id) || S.nq.includes(id) || S.ncur?.id === id);
  const groups: Record<Room, ChoiceItem[]> = {
    "703": [
      tr("請欣怡（703）", "Ask Hsin-Yi (703)"),
      t("wu_mon", tr("接監視器", "Hook up monitor")),
      t("wu_iv2", tr("兩條 18G", "Two 18Gs")),
      t("wu_labs", tr("抽血＋備血", "Labs + type & cross")),
      t("wu_fluid", tr("LR 500 快速滴注", "LR 500 bolus")),
      t("wu_ppi", "PPI IV"),
      t("wu_ng", tr("放鼻胃管", "NG tube")),
      C(tr("禁食", "NPO"), () => setNpo(ch), S.npo),
      C(tr("停 Clexane", "Hold enoxaparin"), () => setClexane(ch), S.holdClexane),
    ],
    "702": [
      tr("請欣怡（702）", "Ask Hsin-Yi (702)"),
      t("li_scan", tr("膀胱超音波", "Bladder scan")),
      t("li_foley", tr("檢查導尿管", "Check Foley")),
      t("li_lasix", "Lasix 20 mg IV"),
      t("li_fluid", "LR 500"),
    ],
    "701": [
      tr("請欣怡（701）", "Ask Hsin-Yi (701)"),
      t("a_pump", tr("處理點滴機", "Fix IV pump")),
      t("a_stilnox", tr("給 Stilnox 5 mg", "Zolpidem 5 mg")),
      ...(S.delirAt !== null
        ? [t("a_calm", tr("安撫、重新定向", "Calm and reorient")), t("a_restrain", tr("上約束帶", "Restraints"))]
        : []),
    ],
  };
  if (room !== "all") return groups[room];
  return [...groups["703"], "sep", ...groups["702"], "sep", ...groups["701"]];
}

function wuChoices(): ChoiceItem[] {
  if (!wuHere()) return [C(tr("公務機", "Phone"), () => openDialog("phone"))];
  if (S.bleedAt !== null)
    return [
      tr("大出血", "Massive bleed"),
      C(tr("緊急發血 O 型 4U", "Emergency-release 4U type O"), () => crisis("o", "bedC"), S.oBlood),
      C(tr("啟動大量輸血（MTP）", "Activate MTP"), () => crisis("mtp", "bedC"), S.mtp),
      C(tr("輸液加壓、全開", "Pressure bags, wide open"), () => crisis("pressure", "bedC"), S.pressure),
      C(tr("再打兩條大管徑 IV", "Two more large-bore IVs"), () => crisis("iv", "bedC"), S.iv2At !== null),
      "sep",
      C(tr("打給江主任", "Call Dr. Chiang"), () => crisis("vs", "bedC"), S.vsHere),
      C(tr("打給放射科", "Call radiology"), () => crisis("ir", "bedC"), S.irAt !== null),
      C(tr("看監視器", "Monitor"), openWuMonitor),
    ];
  return [
    C(tr("床邊特寫", "Bedside close-up"), () => openZoom({ dlg: "bedC", ...wuZoom() })),
    C(tr("自己量生命徵象", "Take vitals yourself"), () => {
      seeVitals("bedC");
      spend(2);
    }),
    C(tr("看引流管", "Look at the drain"), () => openZoom(drainZoom())),
    C(tr("看尿布", "Check the diaper"), wuStool),
    C(tr("摸肚子、看傷口", "Belly and wound"), wuAbd),
    "sep",
    ...nurseOrderChoices("703"),
    "sep",
    C(tr("公務機（打電話）", "Phone (make a call)"), () => openDialog("phone")),
  ];
}

const dialogs: Level["dialogs"] = {
  bedC: {
    title: tr("703 吳太太", "703 Mrs. Wu"),
    sub: () =>
      S.wuLoc === "ct"
        ? tr("床空著（她在 CT 室）", "Bed empty (she’s in CT)")
        : S.wuLoc === "angio" || S.wuLoc === "angioWait"
          ? tr("床空著（她在放射科）", "Bed empty (she’s in radiology)")
          : S.bleedAt !== null
            ? tr("大出血！", "Massive bleed!")
            : tr("Whipple 術後第 9 天", "Whipple POD 9"),
    pics: () => (wuHere() ? ["wu"] : []),
    ch: "bedC",
    talk: () => wuHere(),
    ph: tr("對吳太太說…", "Say to Mrs. Wu…"),
    onOpen() {
      answer("703");
      clearWait("pg_703");
      if (wuHere() && S.bleedAt === null && !S.esc.firstC) {
        S.esc.firstC = true;
        post(
          "bedC",
          "narr",
          null,
          tr(
            `吳太太看到你，趕快把棉被拉好：「醫生你怎麼來了，我沒事啦……」`,
            `Mrs. Wu sees you and hurries to straighten her blanket: “Doctor, what are you doing here? I’m fine, really…”`,
          ),
        );
      }
      return undefined;
    },
    choices: wuChoices,
    async onTalk(text) {
      await wuTalk(text);
    },
  },
  bedB: {
    title: tr("702 李伯伯", "702 Mr. Li"),
    sub: tr("肝切除術後第 1 天", "Liver resection POD 1"),
    pics: ["li"],
    ch: "bedB",
    talk: true,
    ph: tr("對李伯伯說…", "Say to Mr. Li…"),
    onOpen() {
      answer("702");
      return undefined;
    },
    choices() {
      return [
        C(tr("床邊特寫", "Bedside close-up"), () => openZoom({ dlg: "bedB", ...liZoom() })),
        C(
          S.scannerIn702
            ? tr("膀胱超音波", "Bladder scan")
            : tr("膀胱超音波（掃描儀在走廊）", "Bladder scan (scanner’s in the hall)"),
          () => doScan("bedB", "clerk"),
          !S.scannerIn702 || S.liScan,
        ),
        C(tr("順著導尿管檢查", "Trace the Foley"), () => fixFoley("bedB", "clerk"), S.liFixedAt !== null),
        "sep",
        ...nurseOrderChoices("702"),
        "sep",
        C(tr("公務機（打電話）", "Phone (make a call)"), () => openDialog("phone")),
      ];
    },
    async onTalk(text) {
      await liTalk(text);
    },
  },
  bedA: {
    title: tr("701 鄭伯伯", "701 Mr. Cheng Sr."),
    sub: () =>
      S.zhOut
        ? tr("腹腔鏡膽囊切除術後第 1 天（兒子在護理站）", "Lap chole POD 1 (son is at the nurses’ station)")
        : tr("腹腔鏡膽囊切除術後第 1 天，兒子陪著", "Lap chole POD 1, son at the bedside"),
    pics: () => (S.zhOut ? [] : ["zheng"]),
    ch: "bedA",
    talk: () => !S.zhOut,
    ph: tr("對鄭先生說…", "Say to Mr. Cheng…"),
    onOpen() {
      answer("701");
      return undefined;
    },
    choices() {
      return [
        C(tr("床邊特寫", "Bedside close-up"), () => openZoom({ dlg: "bedA", ...bedAZoom() })),
        C(tr("看點滴機", "Check the IV pump"), () => fixPump("bedA", "clerk"), S.pumpFixedAt !== null),
        "sep",
        ...nurseOrderChoices("701"),
        "sep",
        C(tr("公務機（打電話）", "Phone (make a call)"), () => openDialog("phone")),
      ];
    },
    async onTalk(text) {
      await zhengTalk(text);
    },
  },
  zheng: {
    title: tr("鄭先生", "Mr. Cheng"),
    sub: tr("701 的家屬，跑到護理站來了", "701’s family, out at the nurses’ station"),
    pics: ["zheng"],
    ch: "bedA",
    talk: true,
    ph: tr("對鄭先生說…", "Say to Mr. Cheng…"),
    choices() {
      return [
        C(tr("「我現在跟你過去看」", "“I’ll come with you right now”"), () => {
          me("bedA", tr("我現在跟你過去看。", "I’ll come with you and take a look right now."));
          zhengBack();
        }),
      ];
    },
    async onTalk(text) {
      await zhengTalk(text);
    },
  },
  nurse: {
    title: tr("欣怡", "Hsin-Yi"),
    sub: () => {
      if (S.ncur)
        return tr(
          `正在做：${TASK[S.ncur.id].label}${S.nq.length ? `（還有 ${S.nq.length} 件排隊）` : ""}`,
          `Doing: ${TASK[S.ncur.id].label}${S.nq.length ? ` (${S.nq.length} more queued)` : ""}`,
        );
      return S.nq.length
        ? tr(`還有 ${S.nq.length} 件排隊`, `${S.nq.length} queued`)
        : tr("夜班護理師，今晚一個人顧 12 床", "Night nurse, alone with 12 beds tonight");
    },
    pics: ["gs_nurse"],
    ch: "nurse",
    talk: true,
    ph: tr("對欣怡說，也可以口頭下醫囑", "Talk to Hsin-Yi, or give verbal orders"),
    choices: () => nurseOrderChoices("all"),
    async onTalk(text) {
      await nurseTalk(text);
    },
  },
  phone: {
    title: tr("電話", "Phone"),
    sub: tr("護理站電話／公務機", "Nurses’ station phone / pager"),
    ch: "phone",
    choices() {
      const items: ChoiceItem[] = [
        C(tr("看公務機來電紀錄", "Pager log"), () => openZoom({ dlg: "phone", ...pagerZoom() })),
        "sep",
        C(tr("打給江主任", "Call Dr. Chiang"), () => openDialog("vs"), S.vsHere),
        C(tr("打給刀房（阿凱）", "Call the OR (Kai)"), () => openDialog("r")),
        C(tr("打給放射科值班 R", "Call the radiology resident"), callIr),
        C(tr("打給 CT 室：703 緊急 CTA", "Call CT: 703 emergency CTA"), () => orderCta("phone"), S.ctaAt !== null),
      ];
      if (S.bleedAt !== null)
        items.push(
          "sep",
          tr("血庫", "Blood bank"),
          C(tr("緊急發血 O 型 4U", "Emergency-release 4U type O"), () => crisis("o", "phone"), S.oBlood),
          C(tr("啟動大量輸血（MTP）", "Activate MTP"), () => crisis("mtp", "phone"), S.mtp),
        );
      return items;
    },
  },
  vs: {
    title: tr("江主任", "Dr. Chiang"),
    sub: () =>
      S.vsHere
        ? tr("在病房", "On the ward")
        : S.trip
          ? tr("電話中（開車趕來的路上）", "On the phone (driving in)")
          : tr("電話中", "On the phone"),
    pics: ["gs_vs"],
    ch: "vs",
    talk: true,
    ph: tr("向江主任報告（一句話講重點）…", "Present to Dr. Chiang (get to the point in one sentence)…"),
    keepOpen: true,
    async onTalk(text) {
      await vsTalk(text);
    },
    async onCall(reason) {
      await vsTalk("", reason);
    },
  },
  r: {
    title: tr("刀房 3 房", "OR 3"),
    sub: tr("佩佩拿著電話，阿凱在開刀", "Pei-Pei holds the phone; Kai is operating"),
    pics: ["circ", "gs_r"],
    ch: "r",
    talk: true,
    ph: tr("跟阿凱講（他手上在切腸子）…", "Talk to Kai (he’s mid bowel resection)…"),
    keepOpen: true,
    onOpen() {
      if (!S.esc.rPick) {
        S.esc.rPick = true;
        post(
          "r",
          "npc",
          "circ",
          tr(
            "刀房 3 房。阿凱在刷手檯上，你講，我拿給他聽。",
            "OR 3. Kai’s scrubbed in. Go ahead, I’ll hold the phone to his ear.",
          ),
        );
      }
      return undefined;
    },
    async onTalk(text) {
      await rTalk(text);
    },
  },
  ir: {
    title: tr("宗翰", "Tsung-Han"),
    sub: tr("放射科值班 R", "Radiology resident on call"),
    pics: ["ir"],
    ch: "ir",
    talk: true,
    ph: tr("跟放射科講…", "Talk to radiology…"),
    keepOpen: true,
    choices: () => imageChoices("ir"),
    async onTalk(text) {
      await irTalk(text);
    },
  },
  emr: {
    title: tr("病歷系統", "EMR"),
    sub: tr("703／702／701", "703 / 702 / 701"),
    ch: "emr",
    choices: () => imageChoices("emr"),
    doc: doc(() => {
      S.emrRead.add("703").add("702").add("701");
      return tr(
        [
          ["703 吳太太（62 歲）", "胰頭腺癌，Whipple（PPPD）術後第 9 天。"],
          [
            "703 術後經過",
            "POD3 開始進食。POD5 發燒 38.6，drain 變混濁，drain amylase 3,200 U/L → POPF（grade B），NPO 改 TPN、Tazocin。POD7 CT：胰空腸吻合處旁 3 cm 積液，靠近胃十二指腸動脈（GDA）殘端。POD8 恢復軟食。",
          ],
          ["703 檢驗", "Hb：POD5 11.4 → POD7 11.0。PLT 245k。INR 1.0。Cr 0.7。"],
          [
            "703 用藥",
            "Tazocin 4.5 g q8h；Clexane 40 mg SC qd（21:00 已給）；Pantoprazole 40 mg PO qd；止痛 PCA 已停。無藥物過敏。",
          ],
          [
            "703 護理紀錄",
            "01:50 病人主訴腹瀉一次，解黑便約 200 g，已留尿布；drain 色澤待醫師評估。HR 98。病人表示「不用叫醫生」。",
          ],
          [
            "702 李伯伯（71 歲）",
            "B 肝帶原，肝癌（S6/7, 4 cm），昨日右後葉切除，術中出血 400 mL。術後尿量：18–22 時每小時 50–60 mL；22–02 時共 40 mL。Cr 術前 0.9。",
          ],
          [
            "701 鄭伯伯（82 歲）",
            "膽結石合併慢性膽囊炎，昨日腹腔鏡膽囊切除。過去：高血壓、輕度認知退化。術後第一晚（前天）有夜間譫妄、自拔點滴，紀錄寫「避免使用鎮靜安眠藥」。",
          ],
        ],
        [
          ["703 Mrs. Wu (62)", "Pancreatic head adenocarcinoma, POD 9 after Whipple (PPPD)."],
          [
            "703 post-op course",
            "Diet started POD3. POD5 fever 38.6, drain turned cloudy, drain amylase 3,200 U/L → POPF (grade B); made NPO, started TPN and piperacillin-tazobactam. POD7 CT: 3 cm collection next to the pancreaticojejunostomy, close to the gastroduodenal artery (GDA) stump. POD8 back on a soft diet.",
          ],
          ["703 labs", "Hb: POD5 11.4 → POD7 11.0. PLT 245k. INR 1.0. Cr 0.7."],
          [
            "703 meds",
            "Piperacillin-tazobactam 4.5 g q8h; enoxaparin 40 mg SC qd (given at 21:00); pantoprazole 40 mg PO qd; pain PCA discontinued. NKDA.",
          ],
          [
            "703 nursing note",
            "01:50 patient reports one episode of diarrhea, black stool approx. 200 g, diaper kept; drain color awaiting physician assessment. HR 98. Patient states “no need to call the doctor.”",
          ],
          [
            "702 Mr. Li (71)",
            "HBV carrier, HCC (S6/7, 4 cm), right posterior sectionectomy yesterday, EBL 400 mL. Post-op urine output: 18–22h 50–60 mL/hr; 22–02h 40 mL total. Pre-op Cr 0.9.",
          ],
          [
            "701 Mr. Cheng Sr. (82)",
            "Gallstones with chronic cholecystitis, laparoscopic cholecystectomy yesterday. PMH: hypertension, mild cognitive impairment. First post-op night (the night before last): nocturnal delirium, pulled out his own IV; note says “avoid sedative-hypnotics.”",
          ],
        ],
      );
    }),
  },
  board: {
    title: tr("護理站白板", "Nurses’ station whiteboard"),
    sub: tr("7A 病房", "Ward 7A"),
    ch: "board",
    doc: doc(() => {
      S.boardRead = true;
      return tr(
        [
          ["今晚值班", "護理：欣怡（12 床）｜醫師：R3 阿凱（刀房急刀中）｜clerk：你"],
          ["701 鄭伯伯", "Lap chole POD1。⚠ 前天夜間譫妄"],
          ["702 李伯伯", "Hepatectomy POD1。I/O 注意，Foley"],
          ["703 吳太太", "Whipple POD9。⚠ POPF，drain 注意顏色。禁 NSAID"],
          ["705 陳伯伯", "胃切除 POD12，ICU 轉回，NPO＋TPN，drain 照護中"],
          ["刀房", "02:00 急診：嵌頓疝氣、疑小腸缺血，阿嬤 80 歲（這個月第二個了）"],
          ["提醒", "主治電話：江主任 0912-xxx-xxx（緊急才打！）"],
        ],
        [
          ["On tonight", "Nursing: Hsin-Yi (12 beds) | Doctor: R3 Kai (in the OR, emergency case) | Clerk: you"],
          ["701 Mr. Cheng Sr.", "Lap chole POD1. ⚠ Delirium the night before last"],
          ["702 Mr. Li", "Hepatectomy POD1. Watch I/O, Foley"],
          ["703 Mrs. Wu", "Whipple POD9. ⚠ POPF, watch drain color. No NSAIDs"],
          ["705 Mr. Chen", "Gastrectomy POD12, back from ICU, NPO + TPN, drain care"],
          ["OR", "02:00 from ER: incarcerated hernia, ?ischemic bowel, grandma, 80 (second one this month)"],
          ["Note", "Attending’s phone: Dr. Chiang 0912-xxx-xxx (emergencies only!)"],
        ],
      );
    }),
  },
  cart: {
    title: tr("藥車／急救車", "Med cart / crash cart"),
    sub: tr("藥由欣怡給；這裡看得到庫存", "Hsin-Yi gives the meds; this shows what’s stocked"),
    ch: "cart",
    doc: doc(() =>
      tr(
        [
          ["常備藥", "Pantoprazole、Furosemide、Zolpidem（Stilnox）、Haloperidol、Acetaminophen"],
          ["輸液", "LR、NS、輸液加壓袋"],
          ["急救車", "Epinephrine、Amiodarone、氣管內管、AMBU、除顫器"],
          ["提醒", "下醫囑請跟欣怡說，或在床邊選「請欣怡」。"],
        ],
        [
          ["Stock meds", "Pantoprazole, furosemide, zolpidem, haloperidol, acetaminophen"],
          ["Fluids", "LR, NS, pressure bags"],
          ["Crash cart", "Epinephrine, amiodarone, ET tubes, Ambu bag, defibrillator"],
          ["Note", "Give orders to Hsin-Yi, or pick “Ask Hsin-Yi” at the bedside."],
        ],
      ),
    ),
  },
};

function zhengBack(): void {
  const z = ent("zheng");
  S.zhOut = false;
  walkEnt(z, [4, 2]);
  z.act = "bedA";
  z.onArrive = () => {
    z.face = "left";
  };
  clearWait("zheng");
  if (S.dialog === "zheng") openDialog("bedA");
}

function zhengOut(): void {
  if (S.zhOut || resolved701()) return;
  S.zhOut = true;
  const z = ent("zheng");
  walkEnt(z, [5, 7]);
  z.act = "zheng";
  z.onArrive = () => {
    z.face = "down";
  };
  ask(
    "bedA",
    "zheng",
    tr(
      "醫生！你們到底有沒有人要管？我爸一整晚都沒睡！",
      "Doctor! Is anyone going to deal with this or not? My dad hasn’t slept all night!",
    ),
    {
      id: "zheng",
      text: tr("鄭先生在走廊等你", "Mr. Cheng is waiting for you in the hall"),
      dlg: "zheng",
      clearOn: "talk",
    },
  );
}

/* ---------- level ---------- */

export const GS4: Level = {
  id: "gs-night",
  no: tr("四", "4"),
  short: tr("第四關　夜班", "Level 4 · Night Shift"),
  title: tr("夜班：公務機一直響", "Night Shift: The Pager Won’t Stop"),
  place: tr("外科病房 7A", "Surgical Ward 7A"),
  blurb: tr(
    "凌晨兩點，R 在開急刀，三床同時在 call 你。",
    "2 a.m. The resident is in an emergency case, and three beds are paging you at once.",
  ),
  cast: ["gs_nurse", "wu", "li", "zheng", "gs_r", "gs_vs", "ir"],
  intro: tr(
    [
      "週四凌晨 02:00，外科病房 7A。今晚的 PGY 請病假，R3 阿凱在刀房開急刀（急診上來一個嵌頓疝氣的阿嬤），臨走前丟下一句：「病房先幫我接 call，有事打刀房。」",
      "夜班護理師欣怡一個人顧 12 床。你坐在護理站寫完最後一份紀錄，公務機就震了。",
    ],
    [
      "Thursday, 02:00, Surgical Ward 7A. Tonight’s intern called in sick, and the R3, Kai, is in the OR on an emergency case (a grandma from the ER with an incarcerated hernia). On his way out he tossed you one line: “Cover the ward pages for me. Anything comes up, call the OR.”",
      "The night nurse, Hsin-Yi, has 12 beds on her own. You finish your last note at the nurses’ station, and the pager buzzes.",
    ],
  ),
  start: 2 * 60,
  maxT: 80,
  night: true,
  map: [
    "####################",
    "#aaaaa#bbbbbb#ccccc#",
    "#aaaaa#bbbbbb#ccccc#",
    "#aaaaa#bbbbbb#ccccc#",
    "#aaaaa#bbbbbb#ccccc#",
    "###a#####b######c###",
    "#hhhhhhhhhhhhhhhhhh#",
    "#hhhhhhhhhhhhhhhhhh#",
    "#nnnnnnnhhhhhhhhhhh#",
    "#nnnnnnnhhhhhhhhhhh#",
    "#nnnnnnnhhhhhhhhhhh#",
    "####################",
  ],
  zones: {
    a: ["#c9d1e3", "#b6c0d6", "#bfc8dc"],
    b: ["#c9d1e3", "#b6c0d6", "#bfc8dc"],
    c: ["#d2cde3", "#bdb7d2", "#c7c1da"],
    h: ["#c4bfae", "#b2ac99", "#bbb6a4"],
    n: ["#c7d8cc", "#b3c8b8", "#bdd0c2"],
  },
  windows: [
    [3, 0],
    [10, 0],
    [17, 0],
  ],
  exits: [DOOR],
  chName: {
    pager: tr("公務機", "Pager"),
    bedA: "701",
    bedB: "702",
    bedC: "703",
    nurse: tr("欣怡", "Hsin-Yi"),
    vs: tr("江主任", "Dr. Chiang"),
    r: tr("刀房", "OR"),
    ir: tr("放射科", "Radiology"),
    phone: tr("電話", "Phone"),
    emr: tr("病歷", "EMR"),
    board: tr("白板", "Whiteboard"),
    cart: tr("藥車", "Med cart"),
    sys: tr("7A 病房", "Ward 7A"),
  },
  objects: [
    {
      id: "bedA",
      type: "bed",
      x: 2,
      y: 1,
      h: 2,
      label: tr("701 鄭伯伯", "701 Mr. Cheng Sr."),
      pt: true,
      hair: "#e2e2e8",
      skin: "#e8c4a0",
      sheet: "#c9d6a9",
    },
    { id: "pumpA", type: "pump", x: 3, y: 1, label: tr("701 點滴機", "701 IV pump"), act: "bedA" },
    { id: "curtA", type: "curtain", x: 5, y: 1, h: 2, walk: true },
    {
      id: "bedB",
      type: "bed",
      x: 8,
      y: 1,
      h: 2,
      label: tr("702 李伯伯", "702 Mr. Li"),
      pt: true,
      hair: "#c0c0c8",
      skin: "#dfb088",
      sheet: "#a9c8e8",
    },
    { id: "bedB2", type: "bed", x: 11, y: 1, h: 2, label: tr("702 空床", "702 empty bed") },
    { id: "lock", type: "lockers", x: 10, y: 4, w: 2 },
    { id: "mon", type: "monitorStand", x: 14, y: 1, label: tr("703 監視器", "703 monitor"), act: "mon" },
    { id: "bedC", type: "wubed", x: 15, y: 1, h: 2, label: tr("703 吳太太", "703 Mrs. Wu") },
    { id: "chairC", type: "chair", x: 17, y: 2 },
    { id: "sinkC", type: "sink", x: 18, y: 4, label: tr("洗手台", "Sink") },
    { id: "counter", type: "counter", x: 1, y: 8, w: 6 },
    { id: "emr", type: "computer", x: 3, y: 8, label: tr("電腦：病歷", "Computer: EMR") },
    { id: "phone", type: "phone", x: 6, y: 8, label: tr("護理站電話", "Station phone") },
    { id: "board", type: "bigboard", x: 1, y: 10, w: 2, label: tr("護理站白板", "Whiteboard") },
    { id: "cart", type: "cart", x: 9, y: 10, label: tr("藥車／急救車", "Med / crash cart") },
    { id: "bscan", type: "scanner", x: 11, y: 10, label: tr("膀胱掃描儀", "Bladder scanner") },
    { id: "bench", type: "bench", x: 13, y: 10, w: 2 },
    { id: "water", type: "water", x: 15, y: 10 },
    { id: "plant", type: "plant", x: 18, y: 10 },
  ],
  furniture: FURNITURE,
  labels: tr(
    { nurse: "欣怡", zheng: "鄭先生", vs: "江主任", mon: "703 監視器" },
    { nurse: "Hsin-Yi", zheng: "Mr. Cheng", vs: "Dr. Chiang", mon: "703 monitor" },
  ),
  help: tr(
    {
      bedC: "703 吳太太：Whipple 術後（會打開特寫）",
      bedB: "702 李伯伯：肝切除術後",
      bedA: "701 鄭伯伯：膽囊切除術後，兒子陪著",
      mon: "703 的監視器（要先接上）",
      nurse: "欣怡：口頭醫囑。她一次只能做一件事",
      phone: "打給江主任、刀房、放射科、CT 室；看公務機紀錄",
      emr: "三床的病歷與護理紀錄",
      board: "今晚的值班與各床注意事項",
      cart: "藥車和急救車上有什麼",
      bscan: "推進 702 就能在床邊照",
      zheng: "701 的家屬",
      sinkC: "洗手台",
    },
    {
      bedC: "703 Mrs. Wu: post-Whipple (opens a close-up)",
      bedB: "702 Mr. Li: post liver resection",
      bedA: "701 Mr. Cheng Sr.: post cholecystectomy, son at the bedside",
      mon: "703’s monitor (has to be hooked up first)",
      nurse: "Hsin-Yi: verbal orders. She can only do one thing at a time",
      phone: "Call Dr. Chiang, the OR, radiology, CT; view the pager log",
      emr: "Charts and nursing notes for all three beds",
      board: "Tonight’s staffing and notes for each bed",
      cart: "What’s on the med cart and crash cart",
      bscan: "Wheel it into 702 to scan at the bedside",
      zheng: "701’s family",
      sinkC: "Sink",
    },
  ),
  helpWarn: ["bedC"],
  helpRules: tr(
    [
      "公務機的 call 會排在上方，有倒數條。最吵的不一定最急。",
      "欣怡一次只能做一件事；你一次也只能在一個地方。",
      "阿凱在開刀，很難講清楚。真的需要的話，可以直接打給江主任，但要一句話講重點。",
      "緊急 CTA 和放射科栓塞，要江主任同意。",
    ],
    [
      "Pages line up at the top with a countdown bar. The loudest isn’t always the most urgent.",
      "Hsin-Yi can do one thing at a time, and you can only be in one place at a time.",
      "Kai is operating and hard to get a clear word from. If you really need to, you can call Dr. Chiang directly, but get to the point in one sentence.",
      "An emergency CTA and IR embolization need Dr. Chiang’s approval.",
    ],
  ),
  player: [7, 9],
  ents: () => [
    mkEnt({ k: "gs_nurse", id: "gs_nurse", x: 4, y: 9, act: "nurse", face: "up" }),
    mkEnt({ k: "zheng", id: "zheng", x: 4, y: 2, act: "bedA", face: "left" }),
  ],
  init: (): Vars => ({
    pages: [],
    esc: {},
    wuLoc: "ward",
    monitor: false,
    vitalsSeen: [],
    drainSeen: null,
    stoolSeen: false,
    abdDone: false,
    wuRev: new Set(),
    iv2At: null,
    labsAt: null,
    labsDone: false,
    tcReadyAt: null,
    npo: false,
    holdClexane: false,
    ppiAt: null,
    fluidAt: null,
    ngAt: null,
    rebled: false,
    approved: false,
    ctaAt: null,
    ctaReadAt: null,
    ctaRead: false,
    irAt: null,
    irReadyAt: null,
    angioAt: null,
    angioStarted: null,
    bleedAt: null,
    crisisActs: [],
    oBlood: false,
    mtp: false,
    pressure: false,
    bedAnim: null,
    bedOut: false,
    liScan: false,
    liFixedAt: null,
    lasixAt: null,
    liFluid: false,
    liPulled: false,
    liRev: new Set(),
    scannerIn702: false,
    pumpFixedAt: null,
    stilnoxAt: null,
    delirAt: null,
    delirFix: null,
    zhOut: false,
    zhRev: new Set(),
    nq: [],
    ncur: null,
    nAwayUntil: null,
    rTalks: 0,
    rAdvice: "none",
    vsTalked: false,
    vsAccepted: false,
    vsAcceptAt: null,
    vsArriveAt: null,
    vsHere: false,
    trip: null,
    irTalked: false,
    emrRead: new Set(),
    boardRead: false,
    endAt: null,
    endCode: null,
  }),
  dialogs,
  zooms: {
    bedC: () => (wuHere() ? wuZoom() : null),
    bedB: liZoom,
    bedA: bedAZoom,
  },
  quick: {
    mon: () => {
      if (!wuHere())
        note(tr("703 的床空著，監視器的線垂在地上。", "703’s bed is empty; the monitor leads hang to the floor."));
      else if (!S.monitor)
        note(
          tr(
            "監視器沒接在吳太太身上。可以請欣怡接。",
            "The monitor isn’t on Mrs. Wu. You can ask Hsin-Yi to hook her up.",
          ),
        );
      else openWuMonitor();
    },
    bscan: () => {
      if (S.scannerIn702) {
        note(tr("膀胱掃描儀已經在 702 了。", "The bladder scanner is already in 702."));
        return;
      }
      S.scannerIn702 = true;
      note(
        tr(
          "你把膀胱掃描儀推進 702，停在李伯伯床邊。",
          "You wheel the bladder scanner into 702 and park it by Mr. Li’s bed.",
        ),
      );
      spend(1);
    },
    sinkC: () =>
      note(
        tr(
          "你洗了手。水龍頭的水冰得讓你清醒一點。",
          "You wash your hands. The tap water is cold enough to wake you up a little.",
        ),
      ),
    bedB2: () =>
      note(
        tr(
          "空床，床單摺得整整齊齊。明天早上會有新病人。",
          "An empty bed, sheets folded neatly. A new patient comes in tomorrow morning.",
        ),
      ),
  },

  onStart: () =>
    eventBanner(tr("夜班", "Night Shift"), tr("外科病房 7A・公務機在你手上", "Surgical Ward 7A · you have the pager")),
  tick(t) {
    /* pages */
    if (t === PAGE_A)
      page(
        "A1",
        "701",
        tr(
          "701 鄭伯伯的兒子一直按鈴，很生氣，說他爸爸睡不著，要安眠藥。",
          "701 old Mr. Cheng’s son keeps ringing the bell. He’s angry, says his dad can’t sleep and wants a sleeping pill.",
        ),
        tr("家屬要安眠藥（很生氣）", "Family wants a sleeping pill (angry)"),
        10,
      );
    if (t === PAGE_B)
      page(
        "B1",
        "702",
        tr(
          "702 李伯伯肝切除 POD1，這四個小時尿只有 40 mL。要不要給 Lasix？",
          "702 Mr. Li, liver resection POD1, only 40 mL of urine in the last four hours. Should we give Lasix?",
        ),
        tr("尿少，問要不要 Lasix", "Low urine, asks about Lasix"),
        20,
      );
    if (t === PAGE_C && S.wuLoc === "ward")
      page(
        "C1",
        "703",
        tr(
          "703 吳太太剛剛解了一次黑便，drain 好像有一點紅紅的，HR 102、BP 126/74。她說沒事，叫我不要吵醫生。",
          "703 Mrs. Wu just had one black stool, and the drain looks a little reddish. HR 102, BP 126/74. She says she’s fine and told me not to bother the doctors.",
        ),
        tr("黑便、drain 有點紅", "Black stool, drain a bit red"),
        14,
      );

    /* 701 escalation */
    if (t === 12 && S.pumpFixedAt === null && !S.esc.a2) {
      S.esc.a2 = true;
      page(
        "A2",
        "701",
        tr(
          "701 又按鈴了，鄭先生說再沒人處理他要打 1999 投訴，點滴機也一直叫。",
          "701 rang again. Mr. Cheng says if nobody deals with it he’s calling the 1999 city hotline to complain. The IV pump is still alarming too.",
        ),
        tr("家屬說要投訴", "Family threatening to complain"),
        8,
      );
      S.mood.zheng = (S.mood.zheng ?? 0) + 1;
    }
    if (t === 20 && S.pumpFixedAt === null && !S.esc.a3) {
      S.esc.a3 = true;
      zhengOut();
    }
    if (S.zhOut && S.pumpFixedAt === null && t > 20 && (t - 20) % 9 === 0)
      annoy(
        "zheng",
        tr(
          ["醫生！你到底要不要過來？", "我要寫意見表！你叫什麼名字？", "我已經打 1999 了！"],
          [
            "Doctor! Are you coming or not?",
            "I’m filling out a complaint form! What’s your name?",
            "I’ve already called 1999!",
          ],
        ),
        "bedA",
      );
    if (S.stilnoxAt !== null && S.delirAt === null && t === S.stilnoxAt + 12) delirium();

    /* 702 escalation */
    if (t === 24 && S.liFixedAt === null && !S.esc.b2) {
      S.esc.b2 = true;
      page(
        "B2",
        "702",
        tr(
          "702 李伯伯一直說下面很脹，想爬起來尿尿，尿袋還是沒尿。",
          "702 Mr. Li keeps saying he’s bursting down there and wants to climb up to pee. Still nothing in the bag.",
        ),
        tr("一直說下面很脹", "Says he’s bursting down there"),
        10,
      );
    }
    if (t === 36 && S.liFixedAt === null && !S.liPulled) {
      S.liPulled = true;
      page(
        "B3",
        "702",
        tr(
          "702 李伯伯自己去扯導尿管，尿道口在流血！我先壓著。",
          "702 Mr. Li yanked at his own Foley and the urethral meatus is bleeding! I’m holding pressure.",
        ),
        tr("自己扯導尿管", "Yanked his own Foley"),
        8,
        true,
      );
      finding(
        "702",
        tr(
          "病人因膀胱脹痛自行拉扯導尿管，尿道出血",
          "Patient pulled at his own Foley because of painful bladder distension; urethral bleeding",
        ),
      );
    }

    /* 703 */
    if (t === 16 && S.wuLoc === "ward" && pendingPages().some((p) => p.room === "703") && !S.esc.c1b) {
      S.esc.c1b = true;
      post(
        "pager",
        "npc",
        "gs_nurse",
        tr(
          "學弟，703 你有空去看一下嗎？她剛剛去廁所差點昏倒。",
          "Doc, can you go take a look at 703? She almost passed out going to the bathroom just now.",
        ),
      );
      S.wuRev.add("dizzy");
    }
    if (t === REBLEED && S.angioStarted === null && S.bleedAt === null) {
      S.rebled = true;
      const v = wuVitals();
      if (S.wuLoc === "ward") {
        page(
          "C2",
          "703",
          tr(
            `703 吳太太的 drain 突然又多了 60 mL 鮮紅色的，還有血塊！HR ${v.hr}、BP ${v.sbp}/${v.dbp}，她說頭很暈。`,
            `703 Mrs. Wu’s drain suddenly put out another 60 mL of bright red, with clots! HR ${v.hr}, BP ${v.sbp}/${v.dbp}, she says she’s very dizzy.`,
          ),
          tr("drain 又出鮮血，心跳 114", "Bright red in drain, HR 114"),
          10,
          true,
        );
        S.vitalsSeen.push({ t, v });
      } else
        post(
          "pager",
          "alert",
          "gs_nurse",
          tr(
            "CT 室打來說吳太太 drain 又出了一些鮮血，心跳 114。",
            "CT called: Mrs. Wu’s drain put out some more fresh blood, HR 114.",
          ),
        );
      if (!S.vsTalked)
        addWait({
          id: "callvs",
          text: tr("要不要找江主任？", "Call Dr. Chiang?"),
          icon: tr("？", "?"),
          clearOn: "manual",
        });
    }
    if (S.labsAt !== null && !S.labsDone && t >= S.labsAt + 20) {
      S.labsDone = true;
      const hb = S.rebled && S.labsAt >= REBLEED ? "8.6" : "9.4";
      const txt = tr(
        `CBC：Hb ${hb} g/dL（POD7 11.0），PLT 228k，WBC 12,400；PT INR 1.1，aPTT 31 秒；BUN 31，Cr 0.8（BUN 比前天高）`,
        `CBC: Hb ${hb} g/dL (POD7 11.0), PLT 228k, WBC 12,400; PT INR 1.1, aPTT 31 s; BUN 31, Cr 0.8 (BUN up from two days ago)`,
      );
      finding(tr("703 抽血", "703 labs"), txt);
      post("nurse", "result", "gs_nurse", txt);
    }
    if (S.tcReadyAt === t)
      post(
        "nurse",
        "npc",
        "bank",
        tr(
          "703 吳太太 4U PRBC 配好了，要用隨時來領。",
          "4U PRBC crossmatched for 703 Mrs. Wu. Pick them up whenever you need them.",
        ),
      );

    /* CTA → result */
    if (S.ctaAt !== null && t === S.ctaAt + 3 && S.wuLoc === "ward" && S.bleedAt === null) sendToCt();
    if (S.ctaAt !== null && S.wuLoc === "ct" && t === S.ctaAt + 10 && S.bleedAt === null) {
      S.ctaRead = true;
      S.ctaReadAt = t + 2;
      if (S.angioAt === null) scheduleAngio();
      if (S.angioAt !== null && S.angioAt - t <= 6) {
        S.wuLoc = "angioWait";
        post(
          "pager",
          "npc",
          "gs_nurse",
          tr(
            "CT 照完了，放射科說直接推去血管攝影室等。",
            "CT’s done. Radiology says take her straight to the angio suite to wait.",
          ),
        );
      } else {
        S.wuLoc = "ward";
        bedIn();
        post(
          "pager",
          "npc",
          "gs_nurse",
          tr("CT 照完了，先推回病房等。", "CT’s done. Bringing her back to the ward to wait."),
        );
      }
    }
    if (S.ctaReadAt === t) {
      const txt = tr(
        "CTA：胃十二指腸動脈（GDA）殘端有一顆約 8 mm 的假性動脈瘤，動脈期可見顯影劑外滲；旁邊仍有積液。",
        "CTA: an approx. 8 mm pseudoaneurysm at the gastroduodenal artery (GDA) stump, with contrast extravasation in the arterial phase; adjacent collection persists.",
      );
      finding("CTA", txt);
      post("ir", "result", "ir", txt);
      post(
        "pager",
        "alert",
        "ir",
        tr(
          `放射科：703 的 CTA 是 GDA stump pseudoaneurysm，有在滲。${S.irAt === null ? "要栓的話請你們主治跟我們講。" : "我們這邊快好了。"}`,
          `Radiology: 703’s CTA shows a GDA stump pseudoaneurysm, actively leaking. ${S.irAt === null ? "If you want it embolized, have your attending talk to ours." : "We’re almost ready on our end."}`,
        ),
      );
      scheduleAngio();
    }

    /* angio */
    if (S.angioAt !== null && t >= S.angioAt && S.angioStarted === null && S.bleedAt === null) startAngio();

    /* the big one */
    if (t === BIG_BLEED && S.angioStarted === null && S.bleedAt === null) bigBleed();

    /* VS */
    if (S.trip && !S.trip.cb && S.trip.urg !== "high" && !S.vsHere && t - S.trip.start >= Math.floor(S.trip.eta / 2)) {
      S.trip.cb = true;
      incomingCall("cb", "vs");
    }
    if (S.vsArriveAt === t && !S.vsHere) {
      S.vsHere = true;
      pipSet(null);
      if (S.wuLoc === "ward") {
        spawnAt("gs_vs", "vs", DOOR, [17, 3]);
        eventBanner(tr("江主任到場", "Dr. Chiang arrives"));
        post(
          "bedC",
          "npc",
          "gs_vs",
          S.bleedAt !== null
            ? tr(
                "加壓輸血，叫麻醉，直接推 angio，不行就開。",
                "Pressure-bag the blood, call anesthesia, straight to angio. If that fails, we open.",
              )
            : tr("我來了。drain 給我看。放射科好了沒？", "I’m here. Show me the drain. Is radiology ready?"),
        );
      } else {
        eventBanner(tr("江主任到場", "Dr. Chiang arrives"), tr("直接去放射科", "Went straight to radiology"));
        post(
          "pager",
          "npc",
          "gs_vs",
          tr("我直接到放射科了。你把病房顧好。", "I went straight to radiology. You hold down the ward."),
        );
      }
      if (S.irAt === null) activateIr("vs");
      scheduleAngio();
    }

    nurseTick(t);

    /* resolution after the big bleed */
    if (S.endAt === t) {
      clearWait("bleed");
      if (S.endCode === "A") endA();
      else if (S.bleedAt !== null) {
        if (resusScore() >= 3) endB();
        else endC();
      }
    }
  },
  timeup: () =>
    void playEnding(
      "Z",
      tr("交班", "Sign-out"),
      [
        { text: tr("清晨的第一班電梯「叮」一聲打開。", "The first elevator of the morning opens with a “ding.”") },
        {
          who: "gs_nurse",
          text: tr(
            "學弟，703 的事主任接手了，你先去休息吧。",
            "Doc, the attending’s taken over 703. Go get some rest.",
          ),
        },
      ],
      tr("時間到，主任接手。", "Time’s up. The attending takes over."),
    ),

  hud() {
    const last = S.vitalsSeen.at(-1);
    const pend = pendingPages().length;
    return [
      [tr("公務機", "Pager"), pend ? tr(`${pend} 通還沒去看`, `${pend} unanswered`) : tr("暫時安靜", "Quiet for now")],
      [
        last ? `${clock(last.t)} 703` : tr("703 吳太太", "703 Mrs. Wu"),
        last
          ? tr(`${last.v.sbp}/${last.v.dbp}　HR ${last.v.hr}`, `${last.v.sbp}/${last.v.dbp} · HR ${last.v.hr}`)
          : S.wuLoc === "ward"
            ? tr("還沒看", "Not checked")
            : tr("在放射科", "In radiology"),
      ],
      [
        tr("欣怡", "Hsin-Yi"),
        nurseOut()
          ? tr("在 CT 室", "In CT")
          : S.ncur
            ? TASK[S.ncur.id].label
            : S.nq.length
              ? tr(`${S.nq.length} 件排隊`, `${S.nq.length} queued`)
              : tr("在護理站", "At the station"),
      ],
    ];
  },
  urgent: () => S.rebled || S.bleedAt !== null,
  outcomeText: () =>
    S.bleedAt !== null
      ? tr("703 大出血", "703 massive bleed")
      : S.angioStarted !== null
        ? tr("703 送血管攝影", "703 sent to angio")
        : tr("夜班結束", "Night shift over"),

  drawBelow(c, now) {
    const mx = 14 * T;
    const my = 1 * T;
    c.fillStyle = "#8a90a2";
    c.fillRect(mx + 7, my + 6, 2, 10);
    c.fillStyle = "#3a3f52";
    c.fillRect(mx + 1, my, 14, 9);
    c.fillStyle = "#0f1f1a";
    c.fillRect(mx + 2, my + 1, 12, 7);
    if (S.monitor && wuHere()) {
      const fast = S.rebled || S.bleedAt !== null;
      c.fillStyle = "#5be38a";
      for (let i = 0; i < 12; i++) {
        const ph = (i + Math.floor(now / (fast ? 55 : 100))) % 12;
        c.fillRect(mx + 2 + i, ph === 3 ? my + 2 : ph === 4 ? my + 6 : my + 4, 1, 1);
      }
      if (fast && Math.floor(now / 400) % 2) {
        c.fillStyle = "#ff4d5e";
        c.fillRect(mx + 12, my + 1, 2, 2);
      }
    }
    if (S.bedAnim) {
      const p = bedAnimPos(S.bedAnim, now, WU_HOME);
      if (p) wuBedPaint(c, Math.round(p[0] * T), Math.round(p[1] * T));
      else {
        if (S.bedAnim.dir === "in") S.bedOut = false;
        S.bedAnim = null;
      }
    }
    if (!S.bedAnim) {
      if (!S.bedOut) wuBedPaint(c, WU_HOME[0] * T, WU_HOME[1] * T);
      else DRAW.bed?.(c, WU_HOME[0] * T, WU_HOME[1] * T, {});
    }
    const [sx, sy] = S.scannerIn702 ? [10, 2] : [11, 10];
    DRAW.us?.(c, sx * T, sy * T, {});
    R(c, "#ffd447", sx * T + 5, sy * T + 3, 2, 1);
  },
  drawAbove(c, now) {
    const blink = Math.floor(now / 450) % 2 === 0;
    const doors: [Room, number][] = [
      ["701", 4],
      ["702", 10],
      ["703", 17],
    ];
    for (const [room, x] of doors) {
      const p = pendingPages().filter((pg) => pg.room === room);
      const px = x * T + 4;
      const py = 5 * T + 2;
      c.fillStyle = INK;
      c.fillRect(px - 1, py - 1, 9, 6);
      const urgent = p.some((pg) => DOOR_URGENT.test(pg.text)) || (room === "703" && S.bleedAt !== null);
      c.fillStyle =
        p.length || (room === "703" && S.bleedAt !== null)
          ? blink
            ? urgent
              ? "#ff3b4e"
              : "#ffb02e"
            : "#5a3a2a"
          : "#3a4a46";
      c.fillRect(px, py, 7, 4);
    }
    if (S.pumpFixedAt === null && blink) {
      c.fillStyle = "#ff3b4e";
      c.fillRect(3 * T + 10, 1 * T + 5, 2, 2);
    }
    if (S.bleedAt !== null && S.wuLoc === "ward") {
      dither(c, 15 * T, 3 * T, 32, 8, "#b3263a", 0.35);
    }
  },

  graderCase: tr(
    `病例：外科病房夜班，R3 在刀房開急刀難以聯絡，clerk 接三床的 call。701 鄭伯伯 82 歲膽囊切除 POD1，家屬很生氣要安眠藥；真正原因是點滴機下游阻塞一直叫；病人前天術後有譫妄，給 Zolpidem 會誘發譫妄。702 李伯伯肝切除 POD1 少尿，護理師問要不要 Lasix；真正原因是導尿管被壓折、膀胱漲滿（480 mL），先給 Lasix 是錯的。703 吳太太 Whipple POD9 合併 grade B 胰瘻，01:50 黑便一次、引流管少量鮮血、心跳 100 出頭，病人淡化說不要吵醫生：這是胃十二指腸動脈殘端假性動脈瘤的 sentinel bleed，02:24 會再小出血一次，02:42 會大出血。正確處置：優先處理 703，評估生命徵象、看引流管與大便、兩條大管徑 IV、抽血備血、禁食、停 Clexane，R 聯絡不上就直接用 SBAR 打給主治，經主治同意後排緊急 CTA 並啟動介入放射科栓塞。只給 PPI 觀察是錯誤處置。最吵的 701 最不急但可以兩分鐘解決。`,
    `Case: night shift on the surgical ward. The R3 is in the OR on an emergency case and hard to reach; the clerk is answering pages from three beds. 701 old Mr. Cheng, 82, cholecystectomy POD1: the family is angry and wants a sleeping pill; the real cause is the IV pump alarming nonstop for a downstream occlusion; the patient was delirious after surgery the night before last, and zolpidem will trigger delirium. 702 Mr. Li, liver resection POD1, oliguria; the nurse asks whether to give Lasix; the real cause is a kinked Foley with a full bladder (480 mL), and giving Lasix first is wrong. 703 Mrs. Wu, Whipple POD9 with a grade B pancreatic fistula: at 01:50 one episode of melena, a little fresh blood in the drain, HR just over 100, and the patient plays it down and says not to bother the doctors. This is a sentinel bleed from a pseudoaneurysm of the gastroduodenal artery stump; she will have another small bleed at 02:24 and a massive bleed at 02:42. Correct management: prioritize 703; assess vitals, look at the drain and the stool, two large-bore IVs, labs with type and cross, NPO, hold enoxaparin (Clexane); if the resident can't be reached, call the attending directly using SBAR; with the attending's approval, book an emergency CTA and activate IR for embolization. Giving only a PPI and watching is wrong management. The loudest, 701, is the least urgent but can be solved in two minutes.`,
  ),
  rubric: tr(
    [
      "分流：call 的優先順序（703 > 702 > 701），不被最吵的家屬綁住",
      "辨識 sentinel bleed：Whipple＋胰瘻＋引流管鮮血＋黑便＋心跳上升，不當作潰瘍出血只給 PPI",
      "703 床邊評估：生命徵象、引流管、大便、腹部、頭暈病史（含家屬提供的線索）",
      "703 準備：兩條大管徑 IV、抽血備血、禁食、停抗凝",
      "求援：R 聯絡不上時直接以 SBAR 打給主治；經同意後安排 CTA 與介入放射科",
      "702 少尿：先排除機械性原因（膀胱超音波、導尿管），不先給 Lasix",
      "701：找出睡不著的真正原因（點滴機），避免給高齡、譫妄病史者安眠藥；與生氣的家屬溝通",
    ],
    [
      "Triage: prioritizing the pages (703 > 702 > 701), not getting held hostage by the loudest family",
      "Recognizing the sentinel bleed: Whipple + pancreatic fistula + fresh blood in the drain + melena + rising HR; not treating it as an ulcer bleed with just a PPI",
      "703 bedside assessment: vitals, drain, stool, abdomen, history of dizziness (including the clue the family member gives)",
      "703 preparation: two large-bore IVs, labs with type and cross, NPO, hold anticoagulation",
      "Escalation: when the resident can't be reached, calling the attending directly with SBAR; with approval, arranging CTA and IR",
      "702 oliguria: ruling out mechanical causes first (bladder scan, Foley), not giving Lasix first",
      "701: finding the real reason he can't sleep (the IV pump), avoiding sleeping pills in an elderly patient with a history of delirium; communicating with the angry family",
    ],
  ),
  facts: () => {
    const resp = (room: Room): string => {
      const p = S.pages.find((x) => x.room === room);
      if (!p) return tr("沒有 call", "no page");
      return p.answeredAt !== null
        ? tr(`${p.answeredAt - p.t} 分鐘後到床邊`, `at bedside after ${p.answeredAt - p.t} min`)
        : tr("沒去", "never went");
    };
    return tr(
      [
        `703 call 後：${resp("703")}；702：${resp("702")}；701：${resp("701")}`,
        `703 引流管：${S.drainSeen !== null ? "看過" : "沒看"}；大便：${S.stoolSeen ? "看過" : "沒看"}`,
        `兩條大 IV：${S.iv2At !== null ? clock(S.iv2At) : "沒有"}；備血：${S.labsAt !== null ? clock(S.labsAt) : "沒有"}`,
        `江主任：${S.vsAcceptAt !== null ? `${clock(S.vsAcceptAt)} 同意` : "沒說服"}；放射科：${S.irAt !== null ? clock(S.irAt) : "沒有"}`,
        `PPI：${S.ppiAt !== null ? "給了" : "沒給"}；結局：${
          S.angioStarted !== null && S.bleedAt === null ? "大出血前栓塞" : S.bleedAt !== null ? "大出血" : "未定"
        }`,
        `702：${S.liFixedAt !== null ? "找到導尿管折到" : "沒解決"}${S.lasixAt !== null ? "，給了 Lasix" : ""}`,
        `701：${S.pumpFixedAt !== null ? "處理點滴機" : "點滴機沒處理"}${S.stilnoxAt !== null ? "，給了 Stilnox" : ""}${
          S.delirFix === "restrain" ? "，約束" : ""
        }`,
      ],
      [
        `After the 703 page: ${resp("703")}; 702: ${resp("702")}; 701: ${resp("701")}`,
        `703 drain: ${S.drainSeen !== null ? "looked" : "not looked at"}; stool: ${S.stoolSeen ? "looked" : "not looked at"}`,
        `Two large-bore IVs: ${S.iv2At !== null ? clock(S.iv2At) : "no"}; type and cross: ${S.labsAt !== null ? clock(S.labsAt) : "no"}`,
        `Dr. Chiang: ${S.vsAcceptAt !== null ? `agreed at ${clock(S.vsAcceptAt)}` : "not convinced"}; radiology: ${S.irAt !== null ? clock(S.irAt) : "no"}`,
        `PPI: ${S.ppiAt !== null ? "given" : "not given"}; outcome: ${
          S.angioStarted !== null && S.bleedAt === null
            ? "embolized before the massive bleed"
            : S.bleedAt !== null
              ? "massive bleed"
              : "undecided"
        }`,
        `702: ${S.liFixedAt !== null ? "found the kinked Foley" : "not resolved"}${S.lasixAt !== null ? ", gave Lasix" : ""}`,
        `701: ${S.pumpFixedAt !== null ? "fixed the IV pump" : "IV pump not dealt with"}${S.stilnoxAt !== null ? ", gave zolpidem" : ""}${
          S.delirFix === "restrain" ? ", restraints" : ""
        }`,
      ],
    );
  },
  known,
};
